import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {AutocompleteOption, Categorization, Cell, CellEvent, Column, ItemClicked, Row} from './models/models';
import {SourceComment} from './models/source-comment';
import { Hotkey, HotkeysService } from '@components/shared/hotkeys';
import { KeyboardShortcutsComponent } from '@components/shared/keyboard-shortcuts/keyboard-shortcuts.component';
import {Subscription} from 'rxjs';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe';
import {NgxPopupComponent, NgxPopupService} from '../../shared/ngx-popups/ngx-popups/ngx-popups';
import {ActivatedRoute, Router} from '@angular/router';
import {DocumentFileService} from '../../../services/document-file.service';
import {SpreadingState} from './state/manager';
import {SpreadingTemplateTableViewComponent} from './rows/spreading-template-table-view/spreading-template-table-view.component';
import {SpreadingRowLinkService} from './spreading-row-link.service';
import {RowSelectionActionType} from './state/actions';
import {AlertService} from '../../../services/alert.service';
import {SavingPopupComponent} from '../../shared/popups/saving/saving-popup.component';
import {SharedDataService} from '../../../services/shared-data.service';
import {ReviewQueueItem} from '../../../models/review-queue-item';
import {Company} from '../../../models/company';
import {UserService} from '../../../services/user.service';
import {StatementDataService} from './data/statement-data.service';
import {GoalSeekService} from '../../../services/goal-seek.service';
import {
  ACCEPTABLE_URLS,
  DYNAMIC_ADJUSTMENT_STATEMENT,
  IS_MAC,
  SCENARIO_TYPE_PROFORMA,
  SCENARIO_TYPE_PROJECTION,
  SPREAD_ACTION_ADD_COLUMN,
  SPREAD_ACTION_ADJUSTMENT_CREATED,
  SPREAD_ACTION_CATEGORIZE,
  SPREAD_ACTION_CLEAR_CATEGORIZATION,
  SPREAD_ACTION_COLUMN_METADATA_CHANGED,
  SPREAD_ACTION_CUT_SELECTED,
  SPREAD_ACTION_DELETE_COLUMNS,
  SPREAD_ACTION_DELETE_ROW,
  SPREAD_ACTION_EDIT_A_CELL,
  SPREAD_ACTION_INVERT_ROWS,
  INVERT_ROW_TOOLTIP,
  INVERTED_ROW_TOOLTIP,
  SPREAD_ACTION_PASTE_SELECTED,
  SPREAD_ACTION_ROW_LABEL_CHANGED,
  SPREAD_ACTION_TOGGLE_COLUMN,
  SPREAD_ACTION_TOGGLE_ROW,
  SPREAD_ACTION_UNCATEGORIZE_ROW,
  SUPPORTED_CURRENCIES,
  SUPPORTED_VIEW_SOURCE_FILE_TYPES,
  TAXONOMY_ID_STATEMENT_TYPE_LOOKUP,
  UNKNOWN_ERROR_TOAST_BODY,
  UNKNOWN_ERROR_TOAST_TITLE,
  UNLABELED_ROW_PREFIX,
  LLM_AUTONORM_CATEGORIZATION_FLAG,
  USER_GUIDES,
} from '../../../utils/constants';
import { isVendorDataSpread } from '../../../utils/vendor-data';
import {
  preparationTypeDisplayByKey as spreadHeaderPreparationLabels,
  preparationTypeHistoricalHeaderKeys as spreadHeaderPreparationHistoricalKeys,
  preparationTypeProjectionHeaderKeys as spreadHeaderPreparationProjectionKeys,
  reportingIntervalHeaderOptions as spreadHeaderReportingIntervalOptions,
  scenarioHeaderOptions as spreadHeaderScenarioOptions,
} from './spreading-column-header-display';
import {isRowCategorized, isRowLinkedToTemplate, previousCellRoundedDisplay} from './spreading-display-helpers';
import {CommentService} from '@services/shared/comment/comment.service';
import {Box} from './models/box';
import {ConfirmationPopupComponent} from '../../shared/popups/confirmation/confirmation-popup.component';
import {LaunchDarklyService} from '../../../services/launchdarkly.service';
import {PreviousPeriodsService} from '../../../services/previous-periods.service';
import {Statement} from '../review/human-validation/models/statement';
import {StatementDataCache} from './data/cache';
import {TrackingService} from '../../../services/tracking.service';
import {BorrowerService} from '../../../services/borrower.service';
import {Logger, LoggingService} from '../../../services/logging.service';
import {SpreadingTemplateService} from '../../../services/spreading-template.service';
import {DataFrameService} from '../../../services/dataframes.service';
import {SpreadingHistoryService} from '../../../services/spreading-history.service';
import {UserGuideService} from '@services/user-guide.service';
import {StatementService} from '@services/statement.service';
import {TranslationService} from '@services/translation.service';
import {TranslationViewStateService} from '@services/translation-view-state.service';
import {DigitizationService} from '@services/shared/digitization/digitization.service';
import {Digitization} from './models/digitization';
import {
  PromptForProjectionNameComponent
} from '@components/main/spreading/popups/prompt-for-projection-name/prompt-for-projection-name.component';
import {
  SelectSpreadingTemplateModalComponent
} from './modals/select-spreading-template-modal/select-spreading-template-modal.component';
import {TemplateItem} from "@models/template-item";
import {CommentModalComponent} from "@components/shared/comment-modal/comment-modal.component";
import {CommentDeleteModalComponent} from "@components/shared/comment-delete-modal/comment-delete-modal.component";
import {
  AttestationPopupComponent
} from "@components/main/spreading/modals/attestation-modal/attestation-popup.component";
import {ROUNDING_MAP, RoundingService, ScaleType} from '@services/rounding.service';
import {
  CommentSaveMetadata,
  StatementCommentActions,
  StatementCommentTypes
} from "@services/shared/comment/comment.service";
import {SubmissionRulesService} from "@services/submission-rules.service";
import {
  NavigationRequest,
  SubmissionRulesValidationModalComponent,
  ValidationResult
} from "./modals/submission-rules-validation-modal/submission-rules-validation-modal.component";
import {ValidateSubmissionRuleService} from "@services/validate-submission-rule.service";
import {SubmissionRuleActions} from "@services/template-data.service";

declare var bootstrap: any;

const DOC_PANE_MIN_PERCENT = 19;
const DOC_PANE_MAX_PERCENT = 53;
const FLYOUT_GAP_PX = 6;
const FLYOUT_FALLBACK_HEIGHT_PX = 60;
const FLYOUT_VIEWPORT_MARGIN_PX = 8;
const FLYOUT_LAYOUT_PASSES = 2;
const FLYOUT_ABOVE_CLASS = 'categorization-flyout--above';

@Component({
    selector: 'app-spreading-v2',
    templateUrl: './spreading-v2.component.html',
    styleUrls: ['./spreading-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [GoalSeekService],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class SpreadingV2Component implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(SpreadingTemplateTableViewComponent)
  templateTableView?: SpreadingTemplateTableViewComponent;

  subsArr$: Subscription[] = [];
  // AI disclaimer banner gating flag for the Review Mappings step (AUTO-3408).
  readonly llmAutonormFlag = LLM_AUTONORM_CATEGORIZATION_FLAG;
  state: SpreadingState;
  isDASStatement = false;
  dasRefSnapshot: any;
  stdFrameRefSnapshot: any;
  spreadContainsDASStatement = false;
  loading = true;
  reSpreadInProgress = false;
  alreadyCompleted = false;
  viewMode: 'categorize' | 'preview' | 'alerts' = 'categorize';
  forceIntegrationSync = false;
  reviewQueueItem: ReviewQueueItem = null;
  fileId = -1;
  statementId = -1;
  statementUuid = null;
  relatedStatements: Array<Statement>;
  company: Company = null;
  isError = false;
  viewInNativeLanguage = true;

  hasTemplate = false;
  availableTemplates = [];
  selectedTemplate = null;
  isTemplateChooserDisabled = true;
  templateName = '';
  companyObject: Company;

  editingRowLabelIndex = -1;
  labelEditFocusPane: 'source' | 'template' | null = null;

  cellEditFocusPane: 'source' | 'template' | null = null;
  editingColumnHeaderIndex = -1;
  editingColumnHeader = 'statementDate';

  contextMenuMode: 'row' | 'sourceCell' | 'sourceLabel' | 'sourceTableMenu' | 'dasCell' | 'statementDate' | 'statementDateViewComment' | 'sourceCellViewComment' | 'previousPeriodViewCommentMenu' | 'previousPeriodViewComment' | 'derivedOffsetRow' | '' = '';
  contextMenuRow: Row = null;
  contextMenuColumn: Column = null;
  contextMenuCell: Cell = null;
  contextMenuCellEditFocusPane: 'source' | 'template' = 'source';
  
  contextMenuRowIndex = -1;

  contextMenuIsLabelCell = false;
  contextMenuObjectIndex = -1;
  contextMenuReadonlyCommentText: string = '';

  documentSidebarIsOpen: boolean = false;

  submissionRulesSidebarIsOpen: boolean = true;

  viewSourcePage: number;
  viewSourceBox: Box;

  existingColumns = [];

  enableAccessToDoc = false;
  isVendorData = false;

  madeChanges = false;

  canCategorize = true;
  logger: Logger;

  originalDocumentFileUrl: string;

  contextMenuStyle = {
    'display': 'none',
    'top': '0px',
    'left': '0px',
  };

  dataCache: StatementDataCache;

  enableGoalSeek = false;
  goalSeekLineItem = -1;
  visibleSuggestionsOriginId = null;
  visibleSuggestionsOriginColIdx: number | null = null;

  shouldHideTemplate = false;
  scrollTop = 0;
  scrollLeft = 0;

  UNLABELED_ROW_PREFIX = UNLABELED_ROW_PREFIX;

  mergeTableInsertAtLineItemId = -1;

  cellForComment: Cell;
  rowForComment: Row;

  scenarioTypeProjection = SCENARIO_TYPE_PROJECTION;
  readonly reportingIntervalHeaderOptions = spreadHeaderReportingIntervalOptions;
  readonly scenarioHeaderOptions = spreadHeaderScenarioOptions;
  readonly preparationTypeDisplayByKey = spreadHeaderPreparationLabels;
  readonly preparationTypeHistoricalHeaderKeys = spreadHeaderPreparationHistoricalKeys;
  readonly preparationTypeProjectionHeaderKeys = spreadHeaderPreparationProjectionKeys;
  showSourceEnabledInContextMenu = false;

  noMoreUndos = true;
  noMoreRedos = true;
  undoButtonLoading = false;
  redoButtonLoading = false;

  supportedCurrencies = SUPPORTED_CURRENCIES;
  currency: string;
  receivedValue: string = ""
  justPressedEscape = false;

  attestationText: string = '';
  userAcceptedAttestation: boolean = false;
  storedSubmissionRulesValidationResults: ValidationResult[] = null;
  
  protected readonly SubmissionRuleActions = SubmissionRuleActions;

  docPaneWidthPercent = 40;
  private isResizingDocSplit = false;

  templateSpacerWidthPx = 432; 
  private isResizingTemplateSplit = false;
  private lastTemplateClientX: number | null = null;
  
  hideCategorizedDocumentItems: boolean = false;
  private selectionAnchorIdx = -1;

  hotkeys: Array<Hotkey> = [
    // Invert (change sign) the selected row values
    new Hotkey('alt+i', (evt: KeyboardEvent, combo: string) => {
      this.invertSelectedRows();
      return true;
    }),

    // Add an adjustment row for the selected line item
    new Hotkey('alt+a', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      const selectedRowIdx = this.state.getFirstSelectedRowIndex();
      if (selectedRowIdx < 0) {
        return true;
      }
      this.addAdjustmentRow(selectedRowIdx, this.state.sourceRows[selectedRowIdx]);
      return true;
    }),

    // Add a row below the selected row
    new Hotkey(['ctrl+plus', 'ctrl+='], (evt: KeyboardEvent, combo: string) => {
      const selectedRowIdx = this.state.getLastSelectedItemIndex('row');
      if (selectedRowIdx < 0) {
        return true;
      }
      this.addRowAtIdx(selectedRowIdx + 1);
      return true;
    }),

    // Delete the selected row
    new Hotkey('alt+backspace', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      const selectedRowIdx = this.state.getFirstSelectedRowIndex();
      if (selectedRowIdx < 0) {
        return true;
      }
      if (this.state.getAllSelectedRows().length > 1) {
        this.deleteAllSelectedRows();
        return true;
      }
      this.deleteRowAtIdx(selectedRowIdx);
      return true;
    }),
  ];

  // Keep track of validation errors that the user has ignored, by the "uniqid" (row, column, off by amount).
  ignoredValidationErrors: Array<string> = [];

  private delay = (function() {
    let timer: any = null;
    return function(callback: any, ms: number) {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(callback, ms);
    };
  })();

  constructor(
    private _hotkeysService: HotkeysService,
    private _crd: ChangeDetectorRef,
    private _popupService: NgxPopupService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _documentFileService: DocumentFileService,
    private _userService: UserService,
    private _alertService: AlertService,
    private _sharedDataService: SharedDataService,
    private _statementDataService: StatementDataService,
    private _launchDarklyService: LaunchDarklyService,
    private _trackingService: TrackingService,
    private _loggingService: LoggingService,
    private _goalSeekSerice: GoalSeekService,
    private _dataFrameService: DataFrameService,
    private _borrowerService: BorrowerService,
    private _spreadingTemplateService: SpreadingTemplateService,
    private _spreadingHistoryService: SpreadingHistoryService,
    private _statementService: StatementService,
    public userGuideService: UserGuideService,
    private digitizationService: DigitizationService,
    private _roundingService: RoundingService,
    private _submissionRulesService: SubmissionRulesService,
    public validationSubmissionRuleService: ValidateSubmissionRuleService,
    private _translationService: TranslationService,
    private _previousPeriodsService: PreviousPeriodsService,
    private _rowLinkService: SpreadingRowLinkService,
    private _commentService: CommentService,
    private _translationViewState: TranslationViewStateService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('UserService');
  }

  previousPeriodsFilterPanelOpen = false;

  /**
   * Load the metadata + persisted selection, then resolve & apply
   * payloads for any pre-selected periods. Safe to call repeatedly.
   */
  loadPreviousPeriods(): void {
    if (!this.state || !this.state.statement?.uuid) return;
    const statementUuid = this.state.statement.uuid;
    this._previousPeriodsService.load(statementUuid).subscribe({
      next: (resp) => {
        this.state.setAvailablePreviousPeriods(resp);
        if (this.state.selectedPreviousPeriodKeys.length > 0) {
          this.resolveAndApplyPreviousPeriods(this.state.selectedPreviousPeriodKeys);
        } else {
          this.state.applyPreviousPeriodPayloads([]);
          this._crd.detectChanges();
        }
      },
      error: () => {
        // Feature gate may have flipped server-side; silently no-op.
      },
    });
  }

  private resolveAndApplyPreviousPeriods(periodKeys: string[]): void {
    if (!this.state || !this.state.statement?.uuid) return;
    if (!periodKeys || periodKeys.length === 0) {
      this.state.applyPreviousPeriodPayloads([]);
      this._crd.detectChanges();
      return;
    }
    this._previousPeriodsService.resolve(this.state.statement.uuid, periodKeys).subscribe({
      next: (payloads) => {
        this.state.applyPreviousPeriodPayloads(payloads);
        this._crd.detectChanges();
      },
      error: (err) => {
        // Grid keeps the previously-loaded payloads; selection state is
        // not reverted. Surface a toast so the user knows their filter
        // change wasn't applied.
        console.error('Failed to resolve previous-period payloads', err);
        this._alertService.error('Could not load previous period data. Please try again.');
      },
    });
  }

  /** Apply a new selection (called from the filter panel). Persists + reloads. */
  onPreviousPeriodSelectionChanged(newKeys: string[]): void {
    if (!this.state) return;
    this.state.selectedPreviousPeriodKeys = (newKeys || []).slice();
    const documentFileId = this.state.documentFile?.id;
    const isEmbeddedFlow = this._sharedDataService.embeddedWorkflow$.getValue();
    if (documentFileId != null && !isEmbeddedFlow) {
      this._previousPeriodsService.saveSelection(documentFileId, this.state.selectedPreviousPeriodKeys).subscribe({
        error: (err) => {
          // Persisting the selection is best-effort; failure is silent
          // to the user. Log so it shows up in support sessions.
          console.error('Failed to persist previous-period selection', err);
        },
      });
    }
    this.resolveAndApplyPreviousPeriods(this.state.selectedPreviousPeriodKeys);
  }

  /** Reset to the default reference period only. Spec: "Clear Filters" link. */
  clearPreviousPeriodFilters(): void {
    if (!this.state) return;
    const def = this.state.defaultPreviousPeriodKey;
    this.onPreviousPeriodSelectionChanged(def ? [def] : []);
  }

  /**
   * Whether the "Clear Filters" link should be visible. True when the user's
   * selection differs from the default reference period on its own — including
   * an empty selection, which is a deliberate "show no previous periods" and
   * still needs a route back to the default.
   */
  get canClearPreviousPeriodFilters(): boolean {
    const selected = this.state?.selectedPreviousPeriodKeys ?? [];
    const def = this.state?.defaultPreviousPeriodKey ?? null;
    if (!def) return selected.length > 0;
    return !(selected.length === 1 && selected[0] === def);
  }

  openPreviousPeriodsFilterPanel(): void {
    this.previousPeriodsFilterPanelOpen = true;
  }

  closePreviousPeriodsFilterPanel(): void {
    this.previousPeriodsFilterPanelOpen = false;
  }

  /** Rounded display value for a previous-period cell in the DAS Document Details table. */
  previousPeriodCellDisplay(row: Row, pIdx: number): { rounded: number } | null {
    if (!this.state) {
      return null;
    }
    return previousCellRoundedDisplay(this.state, row, pIdx, this._roundingService);
  }

  /**
   * Tooltip for the previous-period mismatch icon: the prior period's
   * weight and include/exclude selection for the row.
   */
  dasPreviousPeriodCellTooltip(row: Row, pIdx: number): string {
    const cell = row?.previousCells?.[pIdx];
    if (!cell || cell.value == null) {
      return '';
    }
    const parts: Array<string> = [];
    if (this._isDasAddBackRow(row) && cell.previousDasWeightDecimal != null) {
      parts.push(`Weight: ${+(cell.previousDasWeightDecimal * 100).toFixed(2)}%`);
    }
    if (cell.previousDasInclude != null) {
      parts.push(cell.previousDasInclude ? 'Included' : 'Excluded');
    }
    return parts.join(' · ');
  }

  /**
   * True when the prior period's weight or include/exclude selection for
   * this cell differs from the current spread's selection on the row —
   * this surfaces the mismatch warning icon. Prior-only synthetic rows
   * have no current selection to compare against, so any available prior
   * selection is flagged.
   */
  dasPreviousPeriodCellHasMismatch(row: Row, pIdx: number): boolean {
    const cell = row?.previousCells?.[pIdx];
    if (!cell || cell.value == null) {
      return false;
    }
    const isAddBack = this._isDasAddBackRow(row);
    if (!isAddBack && !row?.isExcludableInOtherCalculations) {
      return false;
    }
    if (row.isPreviousOnlyRow) {
      return cell.previousDasWeightDecimal != null || cell.previousDasInclude != null;
    }
    const weightDiffers =
      isAddBack &&
      cell.previousDasWeightDecimal != null &&
      row.weightDecimal != null &&
      cell.previousDasWeightDecimal !== row.weightDecimal;
    const includeDiffers =
      cell.previousDasInclude != null &&
      row.include != null &&
      cell.previousDasInclude !== row.include;
    return weightDiffers || includeDiffers;
  }

  private _isDasAddBackRow(row: Row): boolean {
    return ['AdjustedStandardItem', 'NonAdjustableStandardItem'].includes(row?.itemClass);
  }

  /** True when a previous-period cell in the DAS table carries a read-only comment. */
  hasPreviousPeriodCellComment(row: Row, pIdx: number): boolean {
    return this._previousPeriodCommentsArePopulated(row?.previousCells?.[pIdx]?.sourceComments);
  }

  /** True when a previous-period column header carries a read-only comment. */
  hasPreviousPeriodColumnComment(column: Column): boolean {
    return this._previousPeriodCommentsArePopulated(column?.sourceComments);
  }

  private _previousPeriodCommentsArePopulated(sourceComments: Array<SourceComment> | undefined): boolean {
    return (
      !!sourceComments &&
      sourceComments.length > 0 &&
      this._commentService.commentIsPopulated(sourceComments[0]?.comment)
    );
  }


  resizeDocSplitMouseDown(event: MouseEvent): void {
    if (!this.documentSidebarIsOpen) {
      return;
    }
    event.preventDefault();
    this.isResizingDocSplit = true;
    document.body.style.cursor = 'col-resize';
  }

  @HostListener('window:mousemove', ['$event'])
  onDocWindowMouseMove(event: MouseEvent): void {
    if (!this.isResizingDocSplit || !this.documentSidebarIsOpen) {
      return;
    }

    const container = document.querySelector('.data-container') as HTMLElement | null;
    const docOverlay = document.querySelector('.document-view-overlay') as HTMLElement | null;
    if (!container || !docOverlay) {
      return;
    }

    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }

    // The overlay is not always flush with the container's right edge -- on DAS statements the
    // submission rules panel sits to its right -- so measure the drag against the overlay itself.
    const docRightEdge = docOverlay.getBoundingClientRect().right;
    const newDoc = ((docRightEdge - event.clientX) / rect.width) * 100;

    this.docPaneWidthPercent = this._clampDocPanePercent(newDoc, rect.width);
    this._crd.detectChanges();
  }

  private _clampDocPanePercent(percent: number, containerWidth: number): number {
    const maxPercent = Math.min(DOC_PANE_MAX_PERCENT, this._percentLeftOfSubmissionRules(containerWidth));
    return Math.min(Math.max(percent, DOC_PANE_MIN_PERCENT), maxPercent);
  }

  /** The submission rules panel does not shrink, so the document pane may not grow underneath it. */
  private _percentLeftOfSubmissionRules(containerWidth: number): number {
    const rulesPane = document.querySelector('.submission-rules-pane') as HTMLElement | null;
    const rulesWidth = rulesPane?.getBoundingClientRect().width ?? 0;
    return ((containerWidth - rulesWidth) / containerWidth) * 100;
  }

  @HostListener('window:mouseup')
  onDocWindowMouseUp(): void {
    if (this.isResizingDocSplit) {
      this.isResizingDocSplit = false;
      document.body.style.cursor = 'default';
    }
  }

  ngOnInit() {
    if (this.isBorrowerLockedForRespread()){
      this.showReSpreadInProgressView()
    }
    
    this.addOrRemoveHotkeys();
    this.userGuideService.add(USER_GUIDES.CATEGORIZING_DATA);
    this.userGuideService.add(USER_GUIDES.CORRECTING_DATA);
    this.userGuideService.add(USER_GUIDES.SPREAD_ASSIST);
    this.userGuideService.add(USER_GUIDES.MERGING_DATA);
    this.userGuideService.add(USER_GUIDES.MAKING_ADJUSTMENTS);
    this.userGuideService.add(USER_GUIDES.COMMENTING);
    this.userGuideService.add(USER_GUIDES.TROUBLESHOOTING_SPREAD);
    this.userGuideService.add(USER_GUIDES.SUPPORTED_TAX_RETURNS);
    this.userGuideService.add(USER_GUIDES.REMOVING_DATA);

    // Due to the way angular optimizes, this component will exist as a "singleton"
    // as long as they are clicking between the tabs at the bottom, even though we
    // are reloading data every time. Each load of the screen within the same
    // file needs 90% of the same data (only thing different is the actual statement
    // rows and columns), so to make things snappier we have a cache instance
    // every time you load this screen.
    this.dataCache = new StatementDataCache();
    if (!this._userService.isSpreader()) {
      this._router.navigate(['companies']);
    }
    this._sharedDataService.hideDefaultNavigation();

    this.subsArr$.push(this._route.paramMap.subscribe(params => {
      this.statementUuid = params.get('statement_uuid');
      this.loadData();
    }));

    this.subsArr$.push(this._sharedDataService.appClicks$.subscribe(() => {
      this.hideContextMenu();
    }));

    this._sharedDataService.pageHeaderTitle$.next('Templates');
  }

  _showDocumentFlyoutIfEnabled() {
    const defaultDocViewSetting = this._launchDarklyService.flags['default-show-document-view-in-data-review-and-categorization'];
    if (!defaultDocViewSetting || this.isVendorData) {
      return;
    }
    try {
      const localStorageKey = `docExplorerClosed-${this.fileId}`;
      const documentViewClosed = localStorage.getItem(localStorageKey);
      if (documentViewClosed === 'true') {
        return;
      }
    } catch (ex) {}

    this.openDocumentExplorer(null);
  }

  isBorrowerLockedForRespread(): boolean {
    const resolverData = this._route.snapshot.data
    return resolverData.companyIsBeingReSpread
  }

  showReSpreadInProgressView(): void {
    this.reSpreadInProgress = true;
    this.loading = false;
  }

  showComment(event, row: Row, cell: Cell): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuMode = 'sourceCellViewComment'
    this._crd.detectChanges();
  }

  showColumnComment(event): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuMode = 'statementDateViewComment';
    this._crd.detectChanges();
  }

  handleValue(value: string) {
    this.receivedValue = value
  }

  openCommentModal(row: Row, cell: Cell, editMode: boolean = false) {
    this._popupService.open({
      componentType: CommentModalComponent,
      inputs: {
        cellWithComment: cell,
        rowWithCell: row,
        spreadingState: this.state,
        isStatementDateComment: false,
        statementDate: '',
      },
      outputs: {
        saveComment: () => {
          this.saveStatementWithComment({
            lineItemLabel: row.label,
            commentType: StatementCommentTypes.LINE_ITEM_COMMENT,
            action: editMode ? StatementCommentActions.UPDATE : StatementCommentActions.CREATE
          });
        }
      }
    });
  }

  openCommentDeleteModal(row: Row, cell: Cell) {
    this._popupService.open({
      componentType: CommentDeleteModalComponent,
      cssClass: 'comment-delete-modal',
      inputs: {
        cellWithComment: cell
      },
      outputs: {
        deleteCommentEvent: () => {
          this.saveStatementWithComment({
            lineItemLabel: row.label,
            commentType: StatementCommentTypes.LINE_ITEM_COMMENT,
            action: StatementCommentActions.DELETE
          });
        }
      }
    });
  }

  openStatementDateCommentModal(row: Row, column: Column, editMode: boolean = false) {
    const commentCell = {
      sourceComments: this.hasComment(column) ? column.sourceComments : []
    };
    this._popupService.open({
      componentType: CommentModalComponent,
      inputs: {
        cellWithComment: commentCell,
        rowWithCell: row,
        spreadingState: this.state,
        isStatementDateComment: true,
        statementDate: column.statementDate,
      },
      outputs: {
        saveComment: () => {
          // Only update the column's sourceComments if the user actually saved
          if (commentCell.sourceComments && commentCell.sourceComments[0]?.statementId == this.statementId) {
            column.sourceComments = commentCell.sourceComments;
          }
          this.saveStatementWithComment({
            commentType: StatementCommentTypes.STATEMENT_LEVEL_COMMENT,
            action: editMode ? StatementCommentActions.UPDATE : StatementCommentActions.CREATE
          });
        },
      },
    });
  }

  openStatementDateCommentDeleteModal(column: Column) {
    this._popupService.open({
      componentType: CommentDeleteModalComponent,
      cssClass: "comment-delete-modal",
      inputs: {
        cellWithComment: { sourceComments: column.sourceComments },
      },
      outputs: {
        deleteCommentEvent: () => {
          // Clear the comments from the column
          column.sourceComments = [];
          this.saveStatementWithComment({
            commentType: StatementCommentTypes.STATEMENT_LEVEL_COMMENT,
            action: StatementCommentActions.DELETE
          });
        },
      },
    });
  }

  addOrRemoveHotkeys() {
    this.hotkeys.push(new Hotkey(['ctrl+z', 'command+z'], (evt: KeyboardEvent, combo: string) => {
      this.undoButtonLoading = true;
      this._crd.detectChanges();
      this.undo();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+z', 'command+shift+z'], (evt: KeyboardEvent, combo: string) => {
      this.redoButtonLoading = true;
      this._crd.detectChanges();
      this.redo();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+x', 'ctrl+x'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.cutSelected();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+c', 'ctrl+c'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.copySelected();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+v', 'ctrl+v'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.pasteSelected();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+=', 'command+shift+='], (evt: KeyboardEvent, combo: string) => {
      // Suppresses the browser's zoom-in shortcut, which shares this key combination
      evt.preventDefault();
      this.templateTableView?.expandAll();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+-', 'command+shift+-'], (evt: KeyboardEvent, combo: string) => {
      // Suppresses the browser's zoom-out shortcut, which shares this key combination
      evt.preventDefault();
      this.templateTableView?.collapseAll();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+s', 'command+s'], (evt: KeyboardEvent, combo: string) => {
      evt.preventDefault();
      this.saveAndStay();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+k', 'command+shift+k'], (evt: KeyboardEvent, combo: string) => {
      this.openShortcutsPanel();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+h', 'command+shift+h'], (evt: KeyboardEvent, combo: string) => {
      this.toggleHideCategorizedDocumentRows(evt);
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+pageup', 'command+ctrl+left'], (evt: KeyboardEvent, combo: string) => {
      this.goToAdjacentStatement(-1);
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+pagedown', 'command+ctrl+right'], (evt: KeyboardEvent, combo: string) => {
      this.goToAdjacentStatement(1);
      return true;
    }));
    this.hotkeys.push(new Hotkey('shift+1', (evt: KeyboardEvent, combo: string) => {
      this.templateTableView?.focusFirstChevron();
      return true;
    }));
  }

  openShortcutsPanel(): void {
    this._popupService.open({
      componentType: KeyboardShortcutsComponent,
      cssClass: 'modal-cache-commander',
    });
  }

  goToAdjacentStatement(direction: -1 | 1): void {
    if (!this.relatedStatements?.length) {
      return;
    }
    const currentIdx = this.relatedStatements.findIndex(s => s.id === this.state.statement.id);
    const nextIdx = currentIdx + direction;
    if (currentIdx < 0 || nextIdx < 0 || nextIdx >= this.relatedStatements.length) {
      return;
    }
    this.switchToStatement(this.relatedStatements[nextIdx]);
  }

  /**
   * Function that cuts the selected row objects.
   */
  cutSelected(): void {
    this.stopEditingText();
    this.clearClipboard();

    const selectedRows = this.state.getAllSelectedRows();
    if (selectedRows.length > 0) {
      this.madeAChange();
      this.state.clipboard = JSON.parse(JSON.stringify(selectedRows));
      this.state.clipboard.forEach(row => {
        // Exclude uuid from copy otherwise pasted row will have same uuid
        delete row.uuid;
      });
      this.state.deleteSelectedRows();
      this._alertService.success('Cut row(s)');
    } else {
      this._alertService.info('No rows selected to cut');
    }
    this.syncStatementData();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_CUT_SELECTED);
  }

  /**
   * Function that copies the selected row objects.
   */
  copySelected(): void {
    this.stopEditingText();
    this.clearClipboard();

    const selectedRows = this.state.getAllSelectedRows();
    if (selectedRows.length > 0) {
      this.state.clipboard = JSON.parse(JSON.stringify(selectedRows));
      this.state.clipboard.forEach(row => {
        // Exclude uuid from copy otherwise pasted row will have same uuid
        delete row.uuid;
      });
      this._alertService.success('Copied row(s)');
    } else {
      this._alertService.info('No rows selected to copy');
    }
  }

   /**
   * Function that pastes clipboard contents to the selected row.
   */
  pasteSelected(): void {
    this.stopEditingText();
    if (!this.state.clipboard || this.state.clipboard.length <= 0) {
      this._alertService.info('No items copied to the clipboard!');
      return;
    }

    if (this.state.clipboard[0].hasOwnProperty('cells')) {
      const selectedRowIdx = this.state.getFirstSelectedItemIndex('row');
      if (selectedRowIdx < 0) {
        this._alertService.info('Select a row before pasting');
        return;
      }
      this.madeAChange();
      this.state.clipboard.forEach((row, idx) => {
        const index = selectedRowIdx + idx;
        this.state.replaceRowAtIndex(row, index);
      });
      this.state.rebuildRowReferences();
    }
    this.syncStatementData();
    this.state.calculate();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_PASTE_SELECTED);
  }

  clearClipboard(): void {
    this.state.clipboard = [];
  }

  addRowAtIdx(idx: number) {
    this.madeAChange();
    this.state.addRowsAtIndex(idx, 1);
    this._crd.detectChanges();
  }

  deleteRowAtIdx(idx: number) {
    this.stopEditingText();
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'Delete row?',
        text: `Are you sure you want to delete this row? ("${this.state.sourceRows[idx].label}")`,
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteRowAtIndex(idx);
            this.storeNewState(SPREAD_ACTION_DELETE_ROW);
            this._alertService.success('Row deleted');
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  deleteAllSelectedRows() {
    this.stopEditingText();
    const count = this.state.getAllSelectedRows().length;
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: `Delete ${count} rows?`,
        text: `Are you sure you want to delete these ${count} selected rows?`,
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteSelectedRows();
            this.storeNewState(SPREAD_ACTION_DELETE_ROW);
            this._alertService.success('Rows deleted');
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  madeAChange() {
    this.madeChanges = true;
  }

  /**
   * Synchronizes the working state with the statement data for proper saving.
   */
  private syncStatementData() {
    this.state.statement.rows = this.state.sourceRows;
    this.state.statement.columns = this.state.columns;
  }

  columnHeaderDropDownChanged() {
    this.madeChanges = true;
    this.storeNewState(SPREAD_ACTION_COLUMN_METADATA_CHANGED);
    this.refreshTabStatusAfterEdit();
  }

  addColumnAtIdx(idx: number) {
    this.madeAChange();
    this.stopEditingText();
    this.state.addColumnsAtIndex(idx, 1);
    this.syncStatementData();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_ADD_COLUMN);
  };

  deleteColumn(columnIdx: number) {
    this.stopEditingText();
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'Delete column?',
        text: 'Are you sure you want to delete this column?',
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteColumnAtIndex(columnIdx);
            this.storeNewState(SPREAD_ACTION_DELETE_COLUMNS);
            this._alertService.success('Column deleted!');
            this.syncStatementData();
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  invertSelectedRows(): void {
    this._invertRows(this.state.getAllSelectedRows().filter(row => !row.header));
  }

  invertRow(row: Row, evt: Event): void {
    evt.stopPropagation();
    this._invertRows([row]);
  }

  onTemplateInvertRow(row: Row): void {
    this._invertRows([row]);
  }

  invertContextMenuRow(): void {
    const row = this.contextMenuRow;
    this.hideContextMenu();
    this._invertRows([row]);
  }

  canShowInvertIcon(row: Row): boolean {
    return !this.isDASStatement && this.canCategorize && !row.header && !row.calculated;
  }

  canShowTemplateInvertMenuItem(row: Row): boolean {
    return (
      this.canShowInvertIcon(row) &&
      this.state.sourceRows.includes(row)
    );
  }

  getInvertTooltip(row: Row): string {
    return row.invertValue ? INVERTED_ROW_TOOLTIP : INVERT_ROW_TOOLTIP;
  }

  private _invertRows(rows: Row[]): void {
    this.stopEditingText();
    if (rows.length === 0) {
      return;
    }
    this.madeAChange();
    this.state.invertRows(rows);
    this.storeNewState(SPREAD_ACTION_INVERT_ROWS);
    this._crd.detectChanges();
    this.templateTableView?.revealSourceRows(rows);
  }

  showSourceForCell(evt, row: Row, cellIdx: number) {
    this.stopEditingText();
    const ableToShowSourceBoxOrPage = this.updateSourceCellBox(row, cellIdx);
    if (ableToShowSourceBoxOrPage) {
      this.documentSidebarIsOpen = true;
      this.clearDocExplorerClosedTracking();
      this._crd.detectChanges();
    } else {
      this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
    }
  }

  showSourceForRow(evt, row: Row) {
    this.stopEditingText();
    
    if (row?.lineItemLabelSourceBox) {
      this.resetDocumentViewSourceBox();
      const pageNumber = row.lineItemLabelSourceBox.pageNumber;
      if (this.sourceBoxIsValid(row.lineItemLabelSourceBox, pageNumber)) {
        this.viewSourceBox = row.lineItemLabelSourceBox;
        this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
        this.documentSidebarIsOpen = true;
        this.clearDocExplorerClosedTracking();
        this._crd.detectChanges();
        return;
      } else if (pageNumber > -1) {
        this.viewSourceBox = null;
        this.viewSourcePage = pageNumber;
        this.documentSidebarIsOpen = true;
        this.clearDocExplorerClosedTracking();
        this._crd.detectChanges();
        return;
      }
    }

    this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
  }

  updateSourceCellBox(row: Row, cellIdx: number) {
    this.resetDocumentViewSourceBox();
    const pageNumber = row.cells[cellIdx].pageNumber;
    if (row.cells[cellIdx]?.sourceBox && row.cells[cellIdx].sourceBox !== null && this.sourceBoxIsValid(row.cells[cellIdx].sourceBox, pageNumber)) {
      this.viewSourceBox = row.cells[cellIdx].sourceBox;
      this.viewSourcePage = this.viewSourceBox.pageNumber;

      this._crd.detectChanges();
      return true;
    } else if (pageNumber > -1) {
      this.viewSourceBox = null;
      this.viewSourcePage = row.cells[cellIdx].pageNumber;
      this._alertService.warning('The source cell could not be identified but the source page has been found.', 'Warning', 5);
      this._crd.detectChanges();
      return true;
    }
    return false;
  }

  sourceBoxIsValid(sourceBox, pageNumber) {
    const hasCoord = (c: { x?: number; y?: number } | null | undefined) =>
      c != null && typeof c.x === 'number' && typeof c.y === 'number';
    const pageOk =
      pageNumber == null ||
      pageNumber === -1 ||
      pageNumber === sourceBox?.pageNumber ||
      (pageNumber === 0 && sourceBox?.pageNumber != null);
    return (
      sourceBox?.llc != null &&
      sourceBox?.lrc != null &&
      sourceBox?.ulc != null &&
      sourceBox?.urc != null &&
      hasCoord(sourceBox.llc) &&
      hasCoord(sourceBox.lrc) &&
      hasCoord(sourceBox.ulc) &&
      hasCoord(sourceBox.urc) &&
      pageOk
    );
  }

  setShowSourceEnabledInContextMenu() {
    if (!this.isDASStatement && !this.isVendorData && SUPPORTED_VIEW_SOURCE_FILE_TYPES.includes(this.state.documentFile.fileType)) {
      this.showSourceEnabledInContextMenu = true;
    }
  }

  openMergeTablesInterface() {
    const endingSelectedRow = this.state.endingSelectedRow();
    if (endingSelectedRow && (endingSelectedRow.lineItemId || endingSelectedRow.uuid)) {
      this.mergeTableInsertAtLineItemId = endingSelectedRow.lineItemId;
      this.saveThenNavigateToDedicatedDocumentManagerView('../merge-table', endingSelectedRow.lineItemId, endingSelectedRow.uuid)
    }
  }

  openManageFootnotesInterface() {
    const endingSelectedRow = this.state.endingSelectedRow();
    if (endingSelectedRow && (endingSelectedRow.lineItemId || endingSelectedRow.uuid)) {
      this.saveThenNavigateToDedicatedDocumentManagerView('../manage-footnotes', endingSelectedRow.lineItemId, endingSelectedRow.uuid)
    }
  }

  saveThenNavigateToDedicatedDocumentManagerView(targetPath: string, targetLineItemId: number, targetRowUuid?: string) {
    // so we don't lose data while saving merged table
    this._popupService.open({
      componentType: SavingPopupComponent,
      cssClass: 'modal-confirmation'
    });
    this.saveBeforeNavigatingAway()
      .then(() => {
        this._popupService.closeAll();
        this._alertService.success('Spread saved!');
        this.loading = true;
        this._crd.detectChanges();
        const queryParams: Record<string, any> = { targetLineItem: targetLineItemId };
        if (targetRowUuid) {
          queryParams.targetRowUuid = targetRowUuid;
        }
        this._router.navigate([targetPath], {
          relativeTo: this._route,
          queryParams
        }).finally(() => this.loading = false);
      })
      .catch((err) => {
        this._popupService.closeAll();
        this.loading = false;
        this._crd.detectChanges();
        console.log('Error saving data: ', err);
        this._alertService.error("Unable to save in-progress spread. Make sure the current data is valid and save before proceeding.")
      });
  }

  /** Source row with an open categorization dropdown (non-DAS only; UI lives outside the compact table). */
  get sourceRowWithOpenMappingBox(): Row | null {
    if (this.isDASStatement || !this.state?.sourceRows?.length) {
      return null;
    }
    return this.state.sourceRows.find((r) => r.showMappingBox) ?? null;
  }

  categorizeSelectedRows(asValidation = false) {
    if (!this.canCategorize) {
      return;
    }
    this.madeAChange();
    this.stopEditingText();
    this.getScrollPosition();
    const firstSelectedRow = this.state.getFirstSelectedRow()
    if (!firstSelectedRow || firstSelectedRow.header || firstSelectedRow.itemClass==='supplementalItem') {
      return;
    }

    firstSelectedRow.showMappingBox = true;
    firstSelectedRow.mappingBoxValidation = asValidation;
    this.assignGoalSeekFlag();
    this._crd.detectChanges();
    this._focusOnAutocomplete();
  }

  getScrollPosition() {
    const containerEl = document.getElementById('src-container');
    const scrollEl = containerEl?.querySelector('.src-table-scroll-wrapper');
    const target = scrollEl || containerEl;
    if (target) {
      this.scrollTop = target.scrollTop;
      this.scrollLeft = target.scrollLeft;
    }
  }

  setScrollPosition() {
    const containerEl = document.getElementById('src-container');
    const scrollEl = containerEl?.querySelector('.src-table-scroll-wrapper');
    const target = scrollEl || containerEl;
    if (target) {
      target.scrollTop = this.scrollTop;
      target.scrollLeft = this.scrollLeft;
    }
  }

  toggleDocumentExplorer(evt) {
    if (this.reSpreadInProgress){
      return
    }
    if (this.documentSidebarIsOpen) {
      this.closeDocumentExplorer();
    } else {
      this.openDocumentExplorer(evt);
    }
  }
  
  toggleHideCategorizedDocumentRows(evt) {
    if (this.isDASStatement) {
      return;
    }
    this.hideCategorizedDocumentItems = !this.hideCategorizedDocumentItems;
    this.trackHideCategorizedSelection();
    this._crd.detectChanges();
  }

  /**
   * Track the user's hide-categorized selection in local storage so it remains applied when navigating between tabs.
   */
  trackHideCategorizedSelection() {
    if (this.fileId < 0) {
      return;
    }
    const localStorageKey = `hideCategorized-${this.fileId}`;
    if (this.hideCategorizedDocumentItems) {
      localStorage.setItem(localStorageKey, 'true');
    } else {
      localStorage.removeItem(localStorageKey);
    }
  }

  applyHideCategorizedSelectionIfPersisted() {
    if (this.isDASStatement) {
      this.hideCategorizedDocumentItems = false;
      return;
    }
    if (this.fileId < 0) {
      return;
    }
    try {
      const localStorageKey = `hideCategorized-${this.fileId}`;
      this.hideCategorizedDocumentItems = localStorage.getItem(localStorageKey) === 'true';
    } catch (ex) {}
  }

  toggleSubmissionRulesSidebar(evt) {
    this.submissionRulesSidebarIsOpen = !this.submissionRulesSidebarIsOpen;
    this._crd.detectChanges();
  }


  /**
   * Opens up the document explorer without showing any boxes
   */
  openDocumentExplorer(evt) {
    this.stopEvent(evt);

    // Empty it out
    this.resetDocumentViewSourceBox();
    this.documentSidebarIsOpen = true;
    this.clearDocExplorerClosedTracking();
    this._crd.detectChanges();
  }

  closeDocumentExplorer(skipTracking: boolean = false) {
    this.documentSidebarIsOpen = false;
    if (!skipTracking) {
      this.trackDocumentExplorerClose();
    }
    this._crd.detectChanges();
  }

  /**
   * Track the user action of manually closing the document explorer in local storage so it can remain closed after when
   * navigating between tabs.
   */
  trackDocumentExplorerClose() {
    if (this.fileId < 0) {
      return
    }
    const localStorageKey = `docExplorerClosed-${this.fileId}`;
    localStorage.setItem(localStorageKey, 'true');
  }
  
  clearDocExplorerClosedTracking() {
    const localStorageKey = `docExplorerClosed-${this.fileId}`;
    localStorage.removeItem(localStorageKey);
  }

  uncategorizeSelectedRows() {
    this.madeAChange();
    this.stopEditingText();
    this.state.clearAllCategorizations(this.state.getAllSelectedRows());
    this.assignGoalSeekFlag()
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_UNCATEGORIZE_ROW);
  }

  removeSuggestionFromRow(evt: Event, row: Row) {
    this.madeAChange();
    this.stopEditingText();
    this.state.clearGoalSeekSuggestion(row);
    this._crd.detectChanges();
  }

  resetUI() {
    this.madeChanges = false;
    this.closeDocumentExplorer(true);
    this.resetDocumentViewSourceBox();
  }

  private restoreNavState(): void {
    const { shouldHideTemplate: stateHideTemplate, documentSidebarIsOpen: stateSidebarOpen, ...remainingState }
      = (history.state ?? {}) as { shouldHideTemplate?: boolean; documentSidebarIsOpen?: boolean; [key: string]: any };
    if (stateHideTemplate !== undefined) {
      this.shouldHideTemplate = stateHideTemplate;
    }
    if (stateSidebarOpen !== undefined) {
      this.documentSidebarIsOpen = stateSidebarOpen;
    }
    history.replaceState(remainingState, '');
  }

  getDocumentFileId() {
    const s3KeyParts = this.state.documentFile.name.split('/')
    // a change to the s3 key will have the uuid either as the first or second part of the key string
    return this._userService.getBankId() == s3KeyParts[0] ? s3KeyParts[1] : s3KeyParts[0];

  }

  hideSuggestions() {
    this.visibleSuggestionsOriginId = null;
    this.visibleSuggestionsOriginColIdx = null;
    this._crd.detectChanges();
  }

  goalSeek({ row, colIdx }: { row: Row; colIdx: number }) {
    row.goalSeekInProgress = true;
    setTimeout(() => {
      this.goalSeekLineItem = row.lineItemId;
      const solution = this._goalSeekSerice.solveGoalSeek(this.state.getGoalSeekSourceRows(row, colIdx), -1.0 * row.cells[colIdx].offBy, colIdx);
      if (!solution) {
        this.goalSeekLineItem = -1;
        this._alertService.warning('Our system was unable to find potential solutions. Categorize more items then try again.', 'No Solutions Found');
      } else {
        this.state.clearAllGoalSeekSuggestions();
        this.visibleSuggestionsOriginId = this.goalSeekLineItem;
        this.visibleSuggestionsOriginColIdx = colIdx;
        this.goalSeekLineItem = -1;
        this.state.updateFromGoalSeekSuggestions(solution, this.visibleSuggestionsOriginId);
        this.scrollToVisibleSuggestion();
      }
      row.goalSeekInProgress = false;
      this._crd.detectChanges();
    }, 0)
  }

  scrollToVisibleSuggestion() {
    setTimeout(() => {
      const visibleTooltips = document.querySelectorAll('.goalSeekTooltip');
      if (visibleTooltips.length) {
        const firstVisibleTooltip =   visibleTooltips[0];
        firstVisibleTooltip.scrollIntoView({behavior: "smooth", block: "center"});
      }
    });
  }

  assignGoalSeekFlag() {
    // goal seek for balance sheet is conditional only on the feature flag
    if (this.state.statementType === 'BALANCE_SHEET') {
      this.enableGoalSeek = true;
    } else if (this.state.statementType === 'INCOME_STATEMENT') {
      this.enableGoalSeek = this.state.enableGoalSeekForIncomeStatement();
    } else {
      this.enableGoalSeek = false;
    }
  }

  async loadData(newState: any = null) {
    this.resetUI();
    this.restoreNavState();
    this.loading = true;
    this._crd.detectChanges();
    // Load the statement by ID from the URL
    this._statementDataService.loadStatementByUuid(this.statementUuid).then((statement: Statement) => {
      this.isDASStatement = statement.statementType == DYNAMIC_ADJUSTMENT_STATEMENT;
      this.statementId = statement.id;
      this.fileId = statement.documentFileId;
      return Promise.all([
        this.loadSpreadingState(statement),
        this.loadRelatedStatements(statement),
        this.loadStandardDataFrameRefSnapshot(statement)
      ]);
      }).then(() => {
        this.relatedStatements = this.sortStatements(this.relatedStatements, this.state.taxonomy);
        this.loading = false;
        this.assignGoalSeekFlag();
        this._sharedDataService.pageHeaderTitle$.next(this.state.documentFile.companyName.toString());
        this.hotkeys.forEach(hk => {
          this._hotkeysService.add(hk);
        });

        this.setIsVendorData();
        this.setEnableAccessToDoc();

        if (this.state.rows.length === 0) {
          // No template! Cannot categorize
          this.canCategorize = false;
        } else {
          this.canCategorize = true;
        }

        this._dataFrameService.getPeriodsWithData(this.state.documentFile.company).subscribe(data => {
          const tempArray = [];
          data.forEach(element => {
            tempArray.push({
              preparationType: element.preparationType,
              fiscalYearEnd: element.fiscalYearEnd,
              reportingPeriod: element.value,
              currencyFormat: element.currencyFormat,
            });
          });

          this.existingColumns = tempArray;
        });

        this._crd.detectChanges();

        this._restoreTranslationView();

        this.navigateToSubmissionRuleResult();

        this._showDocumentFlyoutIfEnabled();

        this.applyHideCategorizedSelectionIfPersisted();

        try {
          this._trackingService.trackHumanInLoop({
            type: 'Start',
            step: 'Spreading',
            documentFileId: this.getDocumentFileId(),
            documentCompanyId: this.state.documentFile.company,
            documentTenantId: this._userService.getBankId()
          });
        } catch (err) {
          this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
        }
      }).catch((err) => {
        if (err.error_type === 'ObjectDoesNotExist') {
          this._alertService.error('Review queue item could not be found. It may have been deleted.');
        } else {
          this._alertService.error('Oops! Something went wrong. (' + err.message + ')');
          this.logger.error('Error when loading spreading data: ' + err.message, {'errorObject': err});
        }
    }).catch((err) => {
      this.logger.error('Error when loading spreading data: ' + err.message, {'errorObject': err});
    });
  }

  private loadStandardDataFrameRefSnapshot(statement: Statement): Promise<void> {
    if (this.isDASStatement) {
      const period_key_uuids = statement?.columns.map(col => col?.periodKeyUuid)
      return new Promise<void>((resolve, reject) => {
        this._statementService.loadSTDFrameRefSnapshot(
          statement.companyId, period_key_uuids, statement.documentFileId, statement.spreadingTemplateId).subscribe(
          res => {
            if (this.state) {
              this.state.stdFrameRefSnapshot = res
              this.state.calculate();
              this._crd.detectChanges();
            } else {
              this.stdFrameRefSnapshot = res;
            }
            resolve();
          },
          error => {
            reject(error);
          }
        )
      });
    } else {
      // No need to load std frame ref snapshot for non-DAS statements
      return Promise.resolve();
    }
  }

  swapLanguage() {
    this.viewInNativeLanguage = !this.viewInNativeLanguage;
    this._translationViewState.set(this._documentFileId(), this.viewInNativeLanguage);
    this._crd.detectChanges();
  }

  private _documentFileId(): number | string | null {
    return this.state?.documentFile?.id ?? null;
  }

  private _restoreTranslationView(): void {
    if (!this.hasTranslations()) {
      return;
    }
    this.viewInNativeLanguage = this._translationViewState.get(this._documentFileId());
    this._crd.detectChanges();
  }

  hasTranslations(): boolean {
    return !!(this.state?.statement?.detectedLanguage && this.state.statement.detectedLanguage.language !== 'en');
  }

  rowHasTranslation(row: Row): boolean {
    return !!(row.translatedLabel && row.translatedLabel !== row.label) || !!row.labelEditedByUser;
  }

  loadSpreadingState(statement: Statement) {
    return this._statementDataService.loadState(statement, this.dataCache).then((state: SpreadingState) => {
      [...(state.sourceRows ?? []), ...(state.rows ?? [])].forEach(row => {
        if (row.translatedLabel === 'None' || row.translatedLabel === '') {
          row.translatedLabel = undefined;
        }
      });
      this.state = state;
      if (this.dasRefSnapshot) {
        this.state.dasRefSnapshot = this.dasRefSnapshot;
      }
      if (this.stdFrameRefSnapshot) {
        this.state.stdFrameRefSnapshot = this.stdFrameRefSnapshot;
      }
      this.state.calculate();
      this.loadPreviousPeriods();
      this._crd.detectChanges();
      // only initialize if currently empty.
      if (this._spreadingHistoryService.shouldThisBeInitialized()) {
        this._spreadingHistoryService.initializeNewHistory(state);
      }
      if (state.reviewQueueItem && state.reviewQueueItem.embeddedWorkflow) {
        this._sharedDataService.embeddedWorkflow$.next(state.reviewQueueItem.embeddedWorkflow);
      }

      this.selectedTemplate = this.state.spreadingTemplateId;
      this.loadCompanyInfo(statement);
      this.setShowSourceEnabledInContextMenu();
      this.loadOriginalDocumentDataForDownload();
    });
  }

  loadCompanyInfo(statement: Statement) {
    this._borrowerService.getCompanyById(statement.companyId).subscribe((company: Company) => {
      this.isTemplateChooserDisabled = company.defaultSpreadingTemplate !== null;
      this.companyObject = company;
      // once the templateChooserDisabled flag is set, we can load in the template info
      this.loadTemplateInfo();
    });
  }


  loadTemplateInfo() {
    // TODO potential performance improvement: we do not need to load ALL of the templates data here
    this.subsArr$.push(this._spreadingTemplateService.getTemplates('STANDARD', true).subscribe( (data) => {
      const availableTemps = [];

      data.forEach(template => {
        availableTemps.push({'id': template.id, 'text': template.name, 'items': template.items })
        if (this.state.spreadingTemplateId === template.id) {
          this.templateName = template.name;
          this.attestationText = template?.attestationText;
          this.state.submissionRules = this._submissionRulesService.parseTemplateSubmissionRules(template?.submissionRules);
          this.state.templateHasSubmissionRules = Object.keys(this.state.submissionRules).length > 0;
          if (this.state.templateHasSubmissionRules) {
            this.state.calculate();
          }
        }
      });
      this.availableTemplates = availableTemps;
      this._crd.detectChanges();
      this.runSubmissionRulesValidation();
      // if the default company template has not been chosen and more than one spreading template exists, then show the template selector popup
      if (this.availableTemplates.length > 1 && !this.state.documentFile.initialTemplateSelectionHasBeenMade) {
        this.showSpreadingTemplateSelectionPopup(true);
      }
    }));
  }

  showSpreadingTemplateSelectionPopup(initialTemplateSelection = false) {
    this._popupService.open({
      componentType: SelectSpreadingTemplateModalComponent,
      cssClass: 'modal-lg',
      inputs: {
        availableTemplates: this.availableTemplates,
        defaultTemplateId: this.state.spreadingTemplateId,
        initialTemplateSelection: initialTemplateSelection,
      },
      outputs: {
        selectedTemplateId: (selectedTemplateId: string) => {
          if (selectedTemplateId !== '') {
            this.chooseTemplate(selectedTemplateId);
          } else if (initialTemplateSelection) {
            this.redoManualReview();
          }
        }
      },
    });
  }

  loadRelatedStatements(statement) {
    const fileId = statement.documentFileId;
    return this._statementDataService.loadStatementsForFile(fileId, this.dataCache).then((statements: Array<Statement>) => {
      this.relatedStatements = statements;
      const relatedDASStatement = this.relatedStatements.filter(s => s.statementType === DYNAMIC_ADJUSTMENT_STATEMENT);
      if (relatedDASStatement.length) {
        this.spreadContainsDASStatement = true
        this.loadDASSnapshotData(statement.uuid, fileId)
      }
    });
  }


  loadDASSnapshotData(excludedStatementUuid, documentFileId){
    // TODO: revisit if excludedStatementUuid can be removed so that we can load all DAS snapshots
    // If can remove, we can also remove the buildDasSnapshots in validate-submission-rule.service.ts
    this._statementService.loadDASStatementSnapshot(excludedStatementUuid, documentFileId).subscribe(result => {
      if (result) {
        if (this.state) {
          this.state.dasRefSnapshot = result;
          this.state.calculate();
          this._crd.detectChanges();
        } else {
          this.dasRefSnapshot = result
        }

      }
    }, error => {
      console.error('Failed to load das statement snapshot for calculation reference')
    })
  }

  setIsVendorData() {
    this.isVendorData = isVendorDataSpread(this.state.documentFile);
  }

  setEnableAccessToDoc() {
    this.enableAccessToDoc = !this.isVendorData && this.state.documentFile.fileType.indexOf('PDF_') === 0;
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.loading) {
      return;
    }
    
    if (!this.clickTargetIsLabelCell(event)) {
      this.clearLabelSelection();
    }
    this.cellEditing({
      cellIdx: -1,
      row: null,
    });

    this.stopEditingText();
    this.stopInProgressCategorizations()
  }

  private clickTargetIsLabelCell(event): boolean {
    const target = event?.target as HTMLElement | null;
    return !!target?.closest?.('.line-item');
  }

  private clearLabelSelection(): void {
    this.state?.sourceRows?.forEach((r) => {
      r.labelSelected = false;
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.contextMenuMode) {
        this.hideContextMenu();
        this._crd.detectChanges();
      }
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        if (document.activeElement.tagName === 'SELECT') {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }
  }

  handleCategorizationDropdownEscape(event: KeyboardEvent, row: Row) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      row.showMappingBox = false;
      row.mappingBoxValidation = false;
      this._crd.markForCheck();
      return false;
    }
    return true;
  }

  stopInProgressCategorizations() {
    this.state.sourceRows.forEach(row => {
      row.showMappingBox = false;
    });
    this._crd.detectChanges();
  }

  stopPropagation(event){
    event.preventDefault();
    event.stopPropagation();
  }

  columnHeaderChanged(newValue: string, column: Column, attributeName: string) {
    this.madeAChange();
    column[attributeName] = newValue;
    this.stopEditingText();
    this.storeNewState(SPREAD_ACTION_COLUMN_METADATA_CHANGED);
    this.refreshTabStatusAfterEdit();
  }

  editColumnHeader(colIdx: number, attributeName: string) {
    this.stopEditingText();
    this.editingColumnHeaderIndex = colIdx;
    this.editingColumnHeader = attributeName;
    this._crd.detectChanges();
  }

  stopEditingText() {
    this.editingRowLabelIndex = -1;
    this.labelEditFocusPane = null;
    this.editingColumnHeaderIndex = -1;
    this.hideContextMenu();
  }

  loadOriginalDocumentDataForDownload() {
    this._documentFileService.getDownloadUrlForOriginalDocument(this.state.documentFile.uuid).subscribe(data => {
      this.originalDocumentFileUrl = data.originalFileUrl;
    });
  }

  downloadFile() {
    const url = this.originalDocumentFileUrl;
    const link = document.createElement('a');
    link.href = url;
    link.download = this.state.documentFile.originalDocumentName;
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
  }

  switchToStatement(statement: Statement) {
    this.stopEditingText();
    // If user edits a row label and clicks
    // directly on statement switch handle the save change
    this._crd.detectChanges();
    this.validationSubmissionRuleService.syncCurrentStatementTabStatus(this.state);

    const redirectCommands = ['statements', statement.uuid, 'edit'];
    const redirectExtras = { state: { shouldHideTemplate: this.shouldHideTemplate, documentSidebarIsOpen: this.documentSidebarIsOpen } };
    if (this.madeChanges) {
      this._popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'WARNING',
          text: 'You have unsaved changes. Save and continue?',
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              this.saveAndStay(redirectCommands, redirectExtras);
            }
          }
        },
      });
    } else if (this.isDASStatement) {
      this._popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'WARNING',
          text: 'Please save to ensure the calculated values in this view are available in other statements.',
          cancelButtonText: '',
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              this.saveAndStay(redirectCommands, redirectExtras);
            }
          }
        },
      });
    }
    else {
      this._router.navigate(redirectCommands, redirectExtras);
    }
  }

  ngAfterViewInit() {
    this._crd.detach();

    setTimeout(this.enableTooltips, 4000);
  }

  enableTooltips() {
    // Bootstrap tooltip initialization
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  ngOnDestroy() {
    this._sharedDataService.showDefaultNavigation();

    this.userGuideService.remove(USER_GUIDES.CATEGORIZING_DATA);
    this.userGuideService.remove(USER_GUIDES.CORRECTING_DATA);
    this.userGuideService.remove(USER_GUIDES.SPREAD_ASSIST);
    this.userGuideService.remove(USER_GUIDES.MERGING_DATA);
    this.userGuideService.remove(USER_GUIDES.MAKING_ADJUSTMENTS);
    this.userGuideService.remove(USER_GUIDES.COMMENTING);
    this.userGuideService.remove(USER_GUIDES.TROUBLESHOOTING_SPREAD);
    this.userGuideService.remove(USER_GUIDES.SUPPORTED_TAX_RETURNS);
    this.userGuideService.remove(USER_GUIDES.REMOVING_DATA);

    this.hotkeys.forEach(hk => {
      this._hotkeysService.remove(hk);
    });

    this._spreadingHistoryService.clear(); // so when you leave, we don't keep the history.
    
    // Clear submission rule validation results related
    this.storedSubmissionRulesValidationResults = null;
  }

  getLabelForAdjustment(row: Row): string {
    return row.label + ' (Adj.)';
  }

  addAdjustmentRow(rowIdx: number, row: Row) {
    this.madeAChange();
    this.state.addAdjustmentForRow(rowIdx, this.getLabelForAdjustment(row));
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_ADJUSTMENT_CREATED);
  }

  setFirstBoxFocus(): void {
    const elements = document.getElementsByClassName('source-row-td');

    if (elements.length > 0) {
      // @ts-ignore
      elements[0].focus();
    }
  }

  _focusOnAutocomplete() {
    const itemNameInputClassName = 'autocomplete';
    setTimeout(() => { // shortterm fix to make sure item is there before we find
      const elements = document.getElementsByClassName(itemNameInputClassName);
      if (elements.length > 0) {
        const formulaNameInputBox = elements[0];
        // @ts-ignore
        formulaNameInputBox.focus();
        formulaNameInputBox.addEventListener('keyup', (evt: KeyboardEvent) => {
            evt.preventDefault();
            let delay = 250;
            // If it's keys for navigating the selection we want a faster response than if we're filtering the values.
            if ([27, 38, 40, 13, 9].includes(evt.keyCode)) {
                delay = 10;
            }
            this.delay(() => {
              if (elements.length < 1) {
                return true;
              }
              const domRect = elements[0].getBoundingClientRect();
              const spaceBelow = window.innerHeight - domRect.bottom;
              if (spaceBelow < 200) {
                setTimeout(() => {
                  const autocompleteElements = document.getElementsByClassName('item');
                  if (autocompleteElements.length > 2) {
                    const thirdAutoCompleteElement = autocompleteElements[2];
                    thirdAutoCompleteElement.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
                  }
                }, 0);
              }
              this._crd.detectChanges();
            }, delay);
            return true;
        });

      }
    }, 50);
  }

  rightClickedRow(evt, rowIdx: number, row: Row) {
    this.stopEvent(evt);
    if (this.isDASStatement){
      return
    }
    this.state.deselectAll();
    row.selected = true;
    this.contextMenuMode = 'row';
    this.contextMenuRow = row;
    this.contextMenuObjectIndex = rowIdx;
    this.showContextMenuAtEventLocation(evt);
  }

  rightClickedCell(evt, row: Row, cell: Cell, cellIdx: number, rowIdx: number, showCommentClick=false, editFocusPane: 'source' | 'template' = 'source') {
    this.stopEvent(evt);
    // do we need to handle ManualInputItem differently?
    // like if (this.isDASStatement && row?.itemClass !== "ManualInputItem") ??
    if (this.isDASStatement) {
      this.contextMenuMode = 'dasCell';
    } else if (showCommentClick) {
      this.contextMenuMode = 'sourceCellViewComment';
    } else if (this.state.isDerivedOffsetRow(row)){
      this.contextMenuMode = 'derivedOffsetRow';
    } else {
      this.contextMenuMode = 'sourceCell';
    }
    this.contextMenuRow = row;
    this.contextMenuCell = cell;
    this.contextMenuCellEditFocusPane = editFocusPane;
    this.contextMenuObjectIndex = cellIdx;
    this.showContextMenuAtEventLocation(evt);
    this.cellSelected({
      'row': row,
      'cellIdx': cellIdx,
      'rowIdx': rowIdx,
    })
  }

  /**
   * Opens the unified right-pane source-table menu (Row Level + Cell Level
   * actions). Used for both label cells (cellIdx null) and value cells.
   */
  openSourceTableMenu(evt, row: Row, rowIdx: number, cellIdx: number | null, cell: Cell | null) {
    this.stopEvent(evt);
    this.contextMenuMode = 'sourceTableMenu';
    this.contextMenuRow = row;
    this.contextMenuRowIndex = rowIdx;
    this.contextMenuIsLabelCell = cellIdx == null;
    this.contextMenuObjectIndex = cellIdx ?? -1;
    this.contextMenuCell = cell;
    this.contextMenuCellEditFocusPane = 'source';
    if (cellIdx == null) {
      this.state.deselectAll();
      row.selected = true;
      this.selectLabel(row);
    } else {
      // Value cell: selects the row (SELECT_SINGLE) + column and marks the cell.
      this.cellSelected({ row, cellIdx, rowIdx });
    }
    this.showContextMenuAtEventLocation(evt);
  }

  rightClickedSourceLabel(evt, rowIdx: number, row: Row) {
    // DAS label cells have no menu today; preserve that.
    if (this.isDASStatement) {
      return;
    }
    this.openSourceTableMenu(evt, row, rowIdx, null, null);
  }

  rightClickedSourceCell(evt, row: Row, cell: Cell, cellIdx: number, rowIdx: number) {
    if (this.isDASStatement) {
      this.rightClickedCell(evt, row, cell, cellIdx, rowIdx);
      return;
    }
    this.openSourceTableMenu(evt, row, rowIdx, cellIdx, cell);
  }

  rightClickedStatementDate(evt, columnIdx: number, column: Column) {
    this.stopEvent(evt);
    this.contextMenuMode = "statementDate";
    this.contextMenuColumn = column;
    this.contextMenuObjectIndex = columnIdx;
    this.showContextMenuAtEventLocation(evt);
  }

  hasComment(column: Column): boolean {
    return column.sourceComments && column.sourceComments[0]?.statementId == this.statementId;
  }

  showContextMenuAtEventLocation(evt) {
    const headerOffsetFromBottom = this.isDASStatement ? 150 : 300;
    const nonHeaderOffsetFromBottom = this.isDASStatement ? 150 : 400;
    const distFromBottom = evt.view.innerHeight - evt.pageY;
    let top = evt.pageY;

    // if header row cell, smaller context menu so less offset needed if too low
    if (this.contextMenuMode === 'row' && this.contextMenuRow.header && (distFromBottom < headerOffsetFromBottom)) {
      top = evt.pageY - (headerOffsetFromBottom - distFromBottom);
    }

    // if non header row cell, a larger context menu so less offset needed if too low
    if (this.contextMenuMode === 'row' && !this.contextMenuRow.header && (distFromBottom < nonHeaderOffsetFromBottom)) {
      top = evt.pageY - (nonHeaderOffsetFromBottom - distFromBottom);
    }

    // The unified two-column source-table menu is tall; offset like a non-header row.
    if (this.contextMenuMode === 'sourceTableMenu' && (distFromBottom < nonHeaderOffsetFromBottom)) {
      top = evt.pageY - (nonHeaderOffsetFromBottom - distFromBottom);
    }

    this.contextMenuStyle = {
      'display': 'block',
      'top': `${top}px`,
      'left': `${evt.pageX}px`
    };
    
    if (this.contextMenuMode === 'statementDate') {
      this.contextMenuStyle['left'] = `${evt.pageX-145}px`;
    }

    this._crd.detectChanges();
  }

  hideContextMenu() {
    this.contextMenuStyle = {
      'display': 'none',
      'top': '0px',
      'left': '0px',
    };
    this.contextMenuMode = '';
    this.contextMenuRow = null;
    this.contextMenuColumn = null;
    this.contextMenuCell = null;
    this.contextMenuRowIndex = -1;
    this.contextMenuIsLabelCell = false;
  }
  
  dismissContextMenu(evt: MouseEvent): void {
    evt.preventDefault();
    this.hideContextMenu();
  }

  rowMapped(evt, row: Row) {
    this.itemMapped({
      'sourceRow': row,
      'templateLineItemId': evt.id,
      'isValidation': row.mappingBoxValidation ? true : false,
      'templateLineItemLabel': evt.value,
    });
  }

  itemMapped(c: Categorization) {
    this.madeAChange();
    this.state.categorizeAllSelectedRows(c);
    this.resetMappingBoxConfig();
    this.setScrollPosition();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_CATEGORIZE);
  }

  onRowMouseDown(evt: MouseEvent, rowIdx: number, row: Row): void {
    if (evt.button !== 0 || !this.isMultiSelectClick(evt) || this.isTextEntryTarget(evt)) {
      return;
    }
    // Keeps focus and the browser's text-range selection from interfering with the row selection.
    evt.preventDefault();
    this.clearCellSelectionHighlight();
    this.rowToggled(rowIdx, {
      'augment': false,
      'shift': evt.shiftKey,
      'ctrl': this.controlKey(evt),
      'row': row,
    });
  }

  selectRow(evt, rowIdx: number, row: Row) {
    if (this.isMultiSelectClick(evt)) {
      return;
    }
    this.rowToggled(rowIdx, {
      'augment': evt.altKey,
      'shift': false,
      'ctrl': false,
      'row': row,
    });
  }

  rowToggled(idx: number, info: ItemClicked) {
    this.stopEditingText();
    if (info.shift) {
      this.selectRowRange(this.resolveSelectionAnchor(idx), idx);
    } else if (info.ctrl) {
      if (this.state.isRowSelected(idx)) { // want to unselect it
        this.state.rowSelectionAction(RowSelectionActionType.DESELECT_SINGLE, idx);
      } else { // if not selected, you want to select it
        this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_ADDITIONAL, idx);
      }
      this.selectionAnchorIdx = idx;
    } else {
      this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, idx);
      this.selectionAnchorIdx = idx;
    }
    this.storeNewState(SPREAD_ACTION_TOGGLE_ROW);
    this._crd.detectChanges();
  }

  private resolveSelectionAnchor(clickedIdx: number): number {
    if (this.state.sourceRows[this.selectionAnchorIdx]?.selected) {
      return this.selectionAnchorIdx;
    }
    const firstSelectedIdx = this.state.getFirstSelectedRowIndex();
    return firstSelectedIdx > -1 ? firstSelectedIdx : clickedIdx;
  }

  private selectRowRange(fromIdx: number, toIdx: number): void {
    const start = Math.min(fromIdx, toIdx);
    const end = Math.max(fromIdx, toIdx);
    this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
    for (let i = start; i <= end; i++) {
      if (!this.isRowHidden(this.state.sourceRows[i])) {
        this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_ADDITIONAL, i);
      }
    }
  }

  private isRowHidden(row: Row): boolean {
    return this.hideCategorizedDocumentItems && this.isSourceRowLinkedToTemplate(row);
  }

  private isMultiSelectClick(evt?: MouseEvent): boolean {
    return !!evt && (evt.shiftKey || this.controlKey(evt));
  }

  private isTextEntryTarget(evt: Event): boolean {
    const target = evt.target as HTMLElement | null;
    return !!target?.closest?.('input, textarea, select');
  }

  goToColumn(idx: number) {
    this.stopEditingText();
    // Clear selected rows to indicate a column-focused operation, then select the single target column.
    this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
    this.state.columnSelectionAction(RowSelectionActionType.SELECT_SINGLE, idx);
    this.storeNewState(SPREAD_ACTION_TOGGLE_COLUMN);
    this._crd.detectChanges();
  }

  clearedCategorization(row: Row) {
    this.state.clearCategorization(row);
    this.storeNewState(SPREAD_ACTION_CLEAR_CATEGORIZATION);
    this._crd.detectChanges();
  }

  onSpreadingTemplatePanelStateChanged(state: SpreadingState): void {
    this.state = state;
    this.madeAChange();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_CATEGORIZE);
  }

  scrollManualSourceRowIntoView(row: Row): void {
    if (!row?.uuid) {
      return;
    }
    this._crd.detectChanges();
    setTimeout(() => {
      const el = document.querySelector(
        `[data-source-row-uuid="${row.uuid}"]`
      ) as HTMLElement | null;
      el?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      this.onTemplateSourceRowLabelEditStart(row);
    }, 0);
  }

  editCell(
    row: Row,
    cellIdx: number,
    editFocusPane: 'source' | 'template' = 'source'
  ) {
    // Derived offset rows are read-only; block keyboard/context-menu edit entry (CITI-1274).
    if (this.state.isDerivedOffsetRow(row)) {
      return;
    }
    this.cellEditing({
      row,
      cellIdx,
      editFocusPane,
    });
  }

  editCellAndStopEvent(evt, row: Row, cellIdx: number) {
    this.receivedValue = this.contextMenuCell.rawText
    this.stopEvent(evt);
    this.editCell(row, cellIdx, this.contextMenuCellEditFocusPane);
  }

  cellEditing(cellEvt: CellEvent) {
    this.stopEditingText();
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
      r.cellSelectedIndex = -1;
    });

    if (cellEvt.row) {
      cellEvt.row.cellSelectedIndex = cellEvt.cellIdx;
      cellEvt.row.cellEditingIndex = cellEvt.cellIdx;
      this.cellEditFocusPane = cellEvt.editFocusPane ?? 'source';
    } else {
      this.cellEditFocusPane = null;
    }

    this.state.calculate();
    this._crd.detectChanges();
  }

  highlightCell(row: Row, cellIdx: number, rowIdx: number, evt?: MouseEvent) {
    if (this.isMultiSelectClick(evt)) {
      return;
    }
    this.stopEditingText();
    this.stopInProgressCategorizations();
    this.cellSelected({
      'row': row,
      'cellIdx': cellIdx,
      'rowIdx': rowIdx,
    });
  }

  onTemplateSourceRowLabelClick(row: Row): void {
    this.clickLineItemLabel(row);
    if (row?.uuid) {
      this._rowLinkService.scrollRowIntoView(`[data-source-row-uuid="${row.uuid}"]`);
    }
  }

  isSourceRowLinkedToTemplate(row: Row): boolean {
    return isRowLinkedToTemplate(this.state, row);
  }

  viewCategorizationTarget(row: Row, event?: Event): void {
    event?.stopPropagation();
    if (!isRowCategorized(row)) {
      return;
    }
    this.templateTableView?.revealSourceRow(row);
  }

  /**
   * Places the categorization flyout with fixed positioning so the table's
   * scroll containers cannot clip it or cover it with neighbouring rows.
   */
  positionCategorizationFlyout(event: MouseEvent): void {
    const wrap = event.currentTarget as HTMLElement;
    const flyout = wrap.querySelector<HTMLElement>('.categorization-flyout');
    if (!flyout) {
      return;
    }
    const iconRect = wrap.getBoundingClientRect();
    const iconCenter = iconRect.left + iconRect.width / 2;
    this.centerFlyoutInViewport(flyout, iconCenter);
    const flyoutHeight = flyout.offsetHeight || FLYOUT_FALLBACK_HEIGHT_PX;
    const hasRoomBelow = iconRect.bottom + flyoutHeight + FLYOUT_GAP_PX <= window.innerHeight;
    flyout.classList.toggle(FLYOUT_ABOVE_CLASS, !hasRoomBelow);
    const preferredTop = hasRoomBelow
      ? iconRect.bottom + FLYOUT_GAP_PX
      : iconRect.top - flyoutHeight - FLYOUT_GAP_PX;
    const maxTop = window.innerHeight - flyoutHeight - FLYOUT_VIEWPORT_MARGIN_PX;
    flyout.style.top = `${Math.max(FLYOUT_VIEWPORT_MARGIN_PX, Math.min(preferredTop, maxTop))}px`;
  }

  /**
   * A fixed flyout's width (and so its wrapped height) depends on its `left`, so the
   * position is applied before measuring; otherwise the size reflects the previous hover.
   */
  private centerFlyoutInViewport(flyout: HTMLElement, iconCenter: number): void {
    flyout.style.left = `${iconCenter}px`;
    for (let pass = 0; pass < FLYOUT_LAYOUT_PASSES; pass++) {
      const halfWidth = flyout.offsetWidth / 2;
      const minCenter = halfWidth + FLYOUT_VIEWPORT_MARGIN_PX;
      const maxCenter = window.innerWidth - halfWidth - FLYOUT_VIEWPORT_MARGIN_PX;
      flyout.style.left = `${Math.max(minCenter, Math.min(iconCenter, maxCenter))}px`;
    }
  }

  clickLineItemLabel(row: Row, evt?: MouseEvent) {
    if (this.isMultiSelectClick(evt)) {
      return;
    }
    this.selectLabel(row);
    this.resetDocumentViewSourceBox();

    if (!this.documentSidebarIsOpen || !row?.lineItemLabelSourceBox) {
      return
    }
    
    if (this.sourceBoxIsValid(row.lineItemLabelSourceBox, row.lineItemLabelSourceBox?.pageNumber)) {
      this.viewSourceBox = row.lineItemLabelSourceBox;
      this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
      this._crd.detectChanges();
    } else if (row.lineItemLabelSourceBox?.pageNumber > -1) {
      this.viewSourceBox = null;
      this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
      this._alertService.warning('The source cell could not be identified but the source page has been found.', 'Warning', 5);
      this._crd.detectChanges();
    }
    
  }

 handleKeyDown(cell, event: KeyboardEvent) {
  if (event.key === 'Escape') {
    this.justPressedEscape = true

    cell.value = this.receivedValue
    cell.rawText = this.receivedValue

    this.cellEditing({
      cellIdx: -1,
      row: null,
    });

    this.stopEditingText();
    this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
    this.stopInProgressCategorizations();

    this.state.calculate();
    this._crd.detectChanges();
    return true;
  }
}

  onSourceCellFocus(row: Row, cellIdx: number, rowIdx: number): void {
    this.highlightCell(row, cellIdx, rowIdx);
  }
  
  releaseCellHighlight(event: FocusEvent): void {
    const cell = event.currentTarget as HTMLElement | null;
    if (cell?.contains(event.relatedTarget as Node | null)) {
      return;
    }
    this.clearCellSelectionHighlight();
  }

  clearCellSelectionHighlight(): void {
    this.state?.sourceRows?.forEach(r => {
      r.cellSelectedIndex = -1;
      r.labelSelected = false;
    });
    this._crd.detectChanges();
  }

  onSourceCellKeydown(row: Row, cellIdx: number, event: KeyboardEvent): void {
    // Ignore events bubbling up from the edit input; only act when the cell itself is focused.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      this.receivedValue = row.cells[cellIdx].rawText;
      this.editCell(row, cellIdx, 'source');
    }
  }

  onSourceLabelKeydown(row: Row, rowIdx: number, event: KeyboardEvent): void {
    // Events from the edit input bubble up; only act when the label cell itself is focused.
    if (event.target !== event.currentTarget) {
      return;
    }
    if ((event.key === 'Enter' || event.key === 'F2') && !this.isSourceRowLabelEditDisabled(row)) {
      event.preventDefault();
      this.handleEditSourceRowLabel(rowIdx, row?.itemClass);
      return;
    }
    if (event.key === 'u' || event.key === 'U') {
      event.preventDefault();
      this.uncategorizeFocusedLabel(row);
    }
  }

  isSourceRowLabelEditDisabled(row: Row): boolean {
    if (row?.isPreviousOnlyRow) {
      return true;
    }
    return (
      (!!row?.labelIsLocked && row.itemClass !== 'ManualInputItem') ||
      (!this.viewInNativeLanguage && this.rowHasTranslation(row)) ||
      this.state.isDerivedOffsetRow(row)
    );
  }

  uncategorizeFocusedLabel(row: Row): void {
    if (this.isDASStatement || !row?.categorizedTo) {
      return;
    }
    this.madeAChange();
    this.stopEditingText();
    this.state.clearAllCategorizations([row]);
    this.assignGoalSeekFlag();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_UNCATEGORIZE_ROW);
  }

  selectLabel(row: Row): void {
    this.state?.sourceRows?.forEach(r => {
      r.cellEditingIndex = -1;
      r.cellSelectedIndex = -1;
      r.labelSelected = false;
    });
    this.cellEditFocusPane = null;
    row.labelSelected = true;

    const rowIdx = this.state?.sourceRows?.indexOf(row) ?? -1;
    if (rowIdx > -1) {
      this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, rowIdx);
      this.selectionAnchorIdx = rowIdx;
    }

    this._crd.detectChanges();
  }

  cellSelected(cellEvt: CellEvent) {
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
      r.cellSelectedIndex = -1;
      r.labelSelected = false;
    });
    this.cellEditFocusPane = null;

    cellEvt.row.cellSelectedIndex = cellEvt.cellIdx;

    // Select the row and column that cell is in
    this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, cellEvt.rowIdx);
    this.selectionAnchorIdx = cellEvt.rowIdx;
    this.state.columnSelectionAction(RowSelectionActionType.SELECT_SINGLE, cellEvt.cellIdx);

    if (this.documentSidebarIsOpen) {
      this.updateSourceCellBox(cellEvt.row, cellEvt.cellIdx);
    }
    this._crd.detectChanges();
  }

  doneEditingCell(newValue: string, row: Row, cellIdx: number) {
    if (this.justPressedEscape) {
      newValue = this.receivedValue
    }
    this.justPressedEscape = false;
    this.cellDoneEditing({
      'rawText': newValue,
      'row': row,
      'value': this.parseCellValueAsNumber(newValue),
      'cellIdx': cellIdx
    });
  }

  parseCellValueAsNumber(value: string): number {
    let noCommasOrWhitespace = value.replace(/[,\s]+/g, '');
    let multiplier = 1.0;
    if (noCommasOrWhitespace[0] === '(' && noCommasOrWhitespace[noCommasOrWhitespace.length - 1] === ')') {
      multiplier = -1.0;
      noCommasOrWhitespace = noCommasOrWhitespace.substring(1, noCommasOrWhitespace.length - 1);
    }

    if (this.isNumber(noCommasOrWhitespace)) {
      const parsed = parseFloat(noCommasOrWhitespace);
      return parsed * multiplier;
    }

    return NaN;
  }

  isNumber(number: string): boolean {
    const numberRegex = /^\s*[+-]?(\d+|\.\d+|\d+\.\d+|\d+\.)(e[+-]?\d+)?\s*$/;
    return numberRegex.test(number);
  }

  cellDoneEditing(cellEvt: CellEvent) {
    let didMakeAChange = false;

    if (cellEvt && cellEvt.rawText && (cellEvt.cellIdx || cellEvt.cellIdx === 0) &&
        cellEvt.row && cellEvt.row.cells &&
        cellEvt.rawText === cellEvt.row.cells[cellEvt.cellIdx].rawText) {
          // skip change if we know for sure that the edit
          // operation caused no change in value
          ;
    } else {
      this.madeAChange();
      didMakeAChange = true;
    }
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
    });
    this.cellEditFocusPane = null;

    const cell = cellEvt.row.cells[cellEvt.cellIdx];
    // Value is a read-only prop. Raw text is the one we actually use.
    cell.value = this._roundingService.unRoundCellValue(
      this.state,
      cellEvt.row,
      cellEvt.value
    );

    // If it's a number, just set the raw text to that number so we "format and save it"
    if (!isNaN(cell.value)) {
      cell.rawText = cell.value.toString();
      cell.roundedRawText = this._roundingService
        .roundCellValue(this.state, cellEvt.row, cell.value)
        .toString();
    } else {
      cell.rawText = cellEvt.rawText;
      cell.roundedRawText = cellEvt.rawText;
    }

    if (isNaN(cell.value)) {
      cell.confidence = 0.0;
    } else {
      cell.confidence = 1.0;
    }

    // Mirror the edit into a linked offset row as an equal-and-opposite
    // value, before recalculating so the offset rolls up correctly (CITI-1274).
    const offsetRow = this.state.mirrorOffsetCellValue(cellEvt.row, cellEvt.cellIdx);

    this.state.calculate();

    if (didMakeAChange) {
      this.storeNewState(SPREAD_ACTION_EDIT_A_CELL);
    }

    this.refreshTabStatusAfterEdit();
    // Update the value asynchronously
    this.saveChangesForCell(cell, cellEvt.row.lineItemId, cellEvt.value);
    if (offsetRow) {
      const offsetCell = offsetRow.cells[cellEvt.cellIdx];
      this.saveChangesForCell(offsetCell, offsetRow.lineItemId, cellEvt.value * -1);
    }
  }

  // CITI-2191: mirrors the pattern="[0-9]+(\.[0-9]+)?" validator on the DAS weight-percentage input
  // (spreading-v2.component.html DAS weight cell). A non-empty weight must be a non-negative decimal;
  // Save/Complete must block exactly when that inline validator would show its error.
  private static readonly VALID_WEIGHT_PERCENTAGE_PATTERN = /^[0-9]+(\.[0-9]+)?$/;

  private hasInvalidDasWeightPercentage(): boolean {
    if (!this.isDASStatement) {
      return false;
    }
    return (this.state?.sourceRows ?? []).some(
      row => row.itemClass === 'AdjustedStandardItem' && this.isWeightPercentageInvalid(row.weightPercentage)
    );
  }

  private isWeightPercentageInvalid(weightPercentage: number | string): boolean {
    if (weightPercentage === null || weightPercentage === undefined || weightPercentage === '') {
      return false;
    }
    // No trimming: the inline pattern validator does not trim, so " 50" / whitespace must block here too.
    return !SpreadingV2Component.VALID_WEIGHT_PERCENTAGE_PATTERN.test(String(weightPercentage));
  }

  private notifyInvalidDasWeightPercentage(
    message = 'Please enter a valid percentage value before saving or completing the spread.'
  ): void {
    this._alertService.error(message, 'Invalid Percentage');
  }

  saveAndStay(redirect = null, redirectExtras: { state?: Record<string, any> } = {}): void {
    if (this.hasInvalidDasWeightPercentage()) {
      this.notifyInvalidDasWeightPercentage();
      return;
    }
    this.save({complete: false, redirect: redirect, redirectExtras: redirectExtras, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false }, false);
  }

  saveStatementWithComment(commentMetadata: CommentSaveMetadata): void {
      if (this.hasInvalidDasWeightPercentage()) {
        this.notifyInvalidDasWeightPercentage(
          'Please fix the invalid percentage value before saving your comment.'
        );
        return;
      }
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false}, false, commentMetadata);
  }
  
  saveAndShowValidationResults(): void {
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false }, false, null, true);
  }

  handleCompleteClick(): void {
    if (this.hasInvalidDasWeightPercentage()) {
      this.notifyInvalidDasWeightPercentage();
      return;
    }
    this.clickout(null);
    this._crd.detectChanges();
    if (this.madeChanges && this.state.templateHasSubmissionRules) {
      this.saveAndShowValidationResults();
      return
    }
    if (this.storedSubmissionRulesValidationResults === null) {
      // If validation is still in progress, show loading indicator when clicking on complete button
      this.loading = true;
      this._crd.detectChanges();
      const checkValidationResults = () => {
        if (this.storedSubmissionRulesValidationResults !== null) {
          // Validation is complete, reset loading and process validation results
          this.loading = false;
          this._crd.detectChanges();
          this.processValidationResults();
        } else {
          setTimeout(checkValidationResults, 100);
        }
      };
      checkValidationResults();
    } else {
      this.processValidationResults();
    }
  }

  private processValidationResults(): void {
    setTimeout(() => {
      // Timeout is needed to avoid complete event happens prior to calculate when user clicking on "Complete" button
      this.updateStoredValidationResultsWithCurrentStatement();
      const hasHardStops = this.storedSubmissionRulesValidationResults.some(result => result.status === 'hard-stop');
      const hasSoftStops = this.storedSubmissionRulesValidationResults.some(result => result.status === 'soft-stop');
      if (hasHardStops || hasSoftStops) {
        this.showSubmissionRulesValidationModal();
      } else {
        this.proceedWithComplete();
      }
    });
  }

  private proceedWithComplete(): void {
    if (this.attestationText) {
      this.openAttestationModal().then((accepted) => {
        this.userAcceptedAttestation = true;
        this.saveAndComplete();
      }, (rejected) => {
        return;
      })
    } else {
      this.saveAndComplete();
    }
  }

  saveAndComplete(): void {
    // Check to see if this statement has projections because this statement might not yet have been saved.
    const currentStatementHasProjections =
      this.state.statement.columns.filter(column => this.isProjectionOrProforma(column['scenario'])).length > 0;

    const companyId = this.state.documentFile.company;
    this.updateCurrencyForCompany();
    this._statementService.otherStatementHasProjections(
      companyId,
      this.state.documentFile.id,
      this.state.statement.id
    ).subscribe((anyStatementBesidesCurrentStatementHasProjections: boolean) => {
      if (currentStatementHasProjections || anyStatementBesidesCurrentStatementHasProjections) {
        this.performSaveWhenProjectionsArePresent();
      } else {
        this.performSaveWhenProjectionsAreNotPresent();
      }
    });
  }

  async openAttestationModal() {
    return new Promise((resolve: any, reject: any) => {
      this._popupService.open({
        componentType: AttestationPopupComponent,
        cssClass: 'modal-attestation',
        inputs: {
          title: 'ATTESTATION',
          attestationText: this.attestationText,
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              resolve();
            } else {
              reject();
            }
          }
        },
      });
    })
  }

  /**
   * Runs submission rules validation and stores the results for later use
   * This method is called after the page is fully loaded and calculations are complete
   */
  private async runSubmissionRulesValidation(reloadDasSnapshots: boolean = false): Promise<void> {
    if (!this.state.templateHasSubmissionRules || !this.relatedStatements) {
      this.storedSubmissionRulesValidationResults = [];
      return;
    }
    try {
      this.storedSubmissionRulesValidationResults = await this.validateSubmissionRules(reloadDasSnapshots);
      this._crd.detectChanges();
      return
    } catch (error) {
      this.logger.error('Error running submission rules validation: ' + error.message, {'errorObject': error});
      this.storedSubmissionRulesValidationResults = [];
      return
    }
  }

  /**
   * Updates stored validation results with current statement's real-time submission rule results
   */
  private updateStoredValidationResultsWithCurrentStatement(): void {
    if (!this.state.templateHasSubmissionRules || !this.state.submissionRules) {
      return;
    }

    const currentStatementResults: ValidationResult[] = [];
    
    for (let colIdx = 0; colIdx < this.state.columns.length; colIdx++) {
      const column = this.state.columns[colIdx];
      const columnValidationResult: ValidationResult = {
        statementId: this.state.statement.id,
        statementType: this.state.statement.statementType,
        statementDate: column.statementDate,
        periodKeyUuid: column.periodKeyUuid,
        status: 'passing',
        rules: []
      };
      
      const rows = this.state.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? this.state.sourceRows : this.state.rows;
      this.processCurrentStatementRowsForValidation(rows, columnValidationResult, colIdx);
      currentStatementResults.push(columnValidationResult);
    }
    
    // Update stored validation results, replacing any existing results for current statement
    if (this.storedSubmissionRulesValidationResults) {
      // Remove any existing results for current statement
      this.storedSubmissionRulesValidationResults = this.storedSubmissionRulesValidationResults.filter(
        result => result.statementId !== this.state.statement.id
      );
    } else {
      this.storedSubmissionRulesValidationResults = [];
    }
    this.storedSubmissionRulesValidationResults.push(...currentStatementResults);
  }

  /**
   * Process current statement rows to collect submission rule validation results
   */
  private processCurrentStatementRowsForValidation(rows: any[], validationResult: ValidationResult, colIdx: number): void {
    for (const row of rows) {
      if (row.cells && row.cells[colIdx]) {
        const cell = row.cells[colIdx];
        if (cell?.submissionRuleResults?.length) {
          for (const ruleResult of cell.submissionRuleResults) {
            validationResult.rules.push({
              message: ruleResult.message,
              action: ruleResult.action,
              rowLabel: row.label,
              cellValue: cell.rawText || cell.value?.toString() || '',
              rowRef: row.ref
            });
            
            // Update validation status
            if (ruleResult.action === 'InsufficientHardStop') {
              validationResult.status = 'hard-stop';
            } else if (ruleResult.action === 'InsufficientSoftStop' && validationResult.status !== 'hard-stop') {
              validationResult.status = 'soft-stop';
            }
          }
        }
      }
      
      // Process child rows recursively
      if (row.children?.length) {
        this.processCurrentStatementRowsForValidation(row.children, validationResult, colIdx);
      }
    }
  }

  /**
   * Validates submission rules for all related statements
   */
  private validateSubmissionRules = async (reloadDasSnapshots: boolean = false): Promise<ValidationResult[]> => {
    // Prepare pre-loaded snapshots to avoid duplicate API calls
    const preLoadedSnapshots: { [cacheKey: string]: { stdFrameRefSnapshot?: Record<string, unknown> } } = {};
    
    // Add the current statement's stdFrameRefSnapshot if it exists
    if (this.state.statement.id && this.state.stdFrameRefSnapshot && Object.keys(this.state.stdFrameRefSnapshot).length > 0) {
      const cacheKey = this.validationSubmissionRuleService.generateCacheKey([this.state.statement]);
      preLoadedSnapshots[cacheKey] = {
        stdFrameRefSnapshot: this.state.stdFrameRefSnapshot
      };
    }
    
    return await this.validationSubmissionRuleService.validateAllStatementsWithFullState(
      this.relatedStatements, 
      this.state.submissionRules,
      this.dataCache,
      this.state,
      preLoadedSnapshots,
      reloadDasSnapshots
    );
  }

  private showSubmissionRulesValidationModal(): void {
    const availableStatements = this.relatedStatements || [];
    const roundingFactor = <ScaleType>(this.state?.reviewQueueItem?.multiplyByFactor || 'ACTUALS');
    this._popupService.open({
      componentType: SubmissionRulesValidationModalComponent,
      cssClass: 'modal-submission-rules-validation',
      inputs: {
        validationResults: this.storedSubmissionRulesValidationResults,
        availableStatements: availableStatements,
        roundingFactor: roundingFactor
      },
      outputs: {
        proceed: () => {
          this.proceedWithComplete();
        },
        navigateToValidationIssue: (navigationRequest: NavigationRequest) => {
          this.navigateToValidationIssue(navigationRequest);
        }
      }
    });
  }

  performSaveWhenProjectionsAreNotPresent() {
    this._borrowerService.getCompanyById(this.state.documentFile.company).subscribe((company: Company) => {
      this.save({
        complete: true,
        redirect: ['companies', company.uuid, 'financials', 'analysis'],
        initialTemplateSelectionHasBeenMade: false,
        reAutonorm: false,
        allowDeletingInsertedRows: true,
        refreshPage: false,
      });
    });
  }

  performSaveWhenProjectionsArePresent() {
    this._borrowerService.getCompanyById(this.state.documentFile.company).subscribe((company: Company) => {
      this.digitizationService
      .get(this.state.documentFile.id, this.state.reviewQueueItem.id)
      .subscribe((digitization: Digitization) => {
        if (!(!!digitization.projectionName) || digitization.projectionName.length === 0) {
          // Get the current projection column data for display
          const projectionColumn = this.state.columns.find(col => col.scenario === 'PROJECTION' || col.scenario === 'PROFORMA');
          const statementDate = projectionColumn?.statementDate || '';
          const reportingInterval = projectionColumn?.reportingInterval || '';

          // Prompt the user for the projection name since is not set and at least one scenario is a projection.
          this._popupService.open({
            componentType: PromptForProjectionNameComponent,
            cssClass: 'modal-confirmation',
            inputs: {
              statementDate: statementDate,
              reportingInterval: reportingInterval
            }
          }).then((popup: NgxPopupComponent) => {
            popup.addEventListener('close', (data: CustomEvent) => {
              if (data.detail === true) {
                this.save({
                  complete: true,
                  redirect: ['companies', company.uuid, 'financials', 'analysis'],
                  initialTemplateSelectionHasBeenMade: false,
                  reAutonorm: false,
                  allowDeletingInsertedRows: true,
                  refreshPage: false,
                });
              }
            }, {once: true});
          }).catch(error => {
            this._alertService.error(`${error.message}`);
          });
        } else {
          this.save({
            complete: true,
            redirect: ['companies', company.uuid, 'financials', 'analysis'],
            initialTemplateSelectionHasBeenMade: false,
            reAutonorm: false,
            allowDeletingInsertedRows: true,
            refreshPage: false,
          });
        }
      });
    });
  }

  openEditProjectionNameModal(column: any): void {
    // Prevent event bubbling
    event?.stopPropagation();

    // Get the current digitization to pass the ID to the modal
    this.digitizationService
      .get(this.state.documentFile.id, this.state.reviewQueueItem?.id)
      .subscribe((digitization: Digitization) => {
        // Open the projection name modal with the current column data and digitization ID
        this._popupService.open({
          componentType: PromptForProjectionNameComponent,
          cssClass: 'modal-confirmation',
          inputs: {
            statementDate: column.statementDate || '',
            reportingInterval: column.reportingInterval || '',
            digitizationId: digitization?.id || 0
          }
        }).then((popup: NgxPopupComponent) => {
          popup.addEventListener('close', (data: CustomEvent) => {
            if (data.detail === true) {
              // Refresh the data or update the UI as needed
              this._crd.detectChanges();
            }
          }, {once: true});
        }).catch(error => {
          this._alertService.error(`${error.message}`);
        });
      }, error => {
        this._alertService.error(`Failed to get digitization data: ${error.message}`);
      });
  }

  saveBeforeNavigatingAway(): Promise<any> {
    // temp save before merge footnotes starts so we don't lose data
    const isComplete = false;
    const shouldForceIntegrationSync = false;
    return this._statementDataService.save(this.state, isComplete, shouldForceIntegrationSync);
  }


  findSupplementalItem(statement: Statement) {
    return statement.rows && statement.rows.some(row => row.itemClass === 'supplementalItem');
  }


  save(options: { complete: boolean, redirect: any, redirectExtras?: { state?: Record<string, any> }, initialTemplateSelectionHasBeenMade: boolean, reAutonorm: boolean, allowDeletingInsertedRows: boolean, refreshPage: boolean } = {complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: false, refreshPage: false},
       reload: boolean = false,
       commentMetadata: CommentSaveMetadata = null,
       showValidationModal: boolean = false) {
    const complete = options.complete;
    const redirect = options.redirect;
    const redirectExtras = options.redirectExtras ?? {};
    const initialTemplateSelectionHasBeenMade = options.initialTemplateSelectionHasBeenMade;
    const reAutonorm = options.reAutonorm;
    const allowDeletingInsertedRows = options.allowDeletingInsertedRows;
    const refreshPage = options.refreshPage;

    this.state.spreadingTemplateId = this.selectedTemplate;

    this._popupService.open({
      componentType: SavingPopupComponent,
      cssClass: 'modal-confirmation'
    }).then(() => {
      return this._statementDataService.save(this.state, complete, this.forceIntegrationSync, initialTemplateSelectionHasBeenMade, reAutonorm, allowDeletingInsertedRows, this.userAcceptedAttestation, commentMetadata);
    }).then((statement) => {
      setTimeout(this.enableTooltips, 1000);

      if(this.findSupplementalItem(statement) && !this.findSupplementalItem(this.state.statement)){
        //synchronous method
        this.state.setStatement(statement);
        this._crd.detectChanges();
      }

      this._popupService.closeAll();
      this.madeChanges = false;
      if (!reAutonorm) {
        this._alertService.success('Spread saved!');
      } else {
        this._alertService.success('Successfully reset mappings!');
      }
      if (complete) {
        this.alreadyCompleted = true;
        this._crd.detectChanges();
      }

      try {
        this._trackingService.trackHumanInLoop({
          type: 'End',
          step: 'Spreading',
          documentFileId: this.getDocumentFileId(),
          documentCompanyId: this.state.documentFile.company,
          documentTenantId: this._userService.getBankId()
        });
      } catch (err) {
        this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
      }

      if (redirect) {
        if (complete) {
          const flow = this._sharedDataService.embeddedWorkflow$.getValue();
          if (flow) {
            ACCEPTABLE_URLS.forEach((acceptable_url) => {
              try {
                window.parent.postMessage({
                  'eventName': 'workflowComplete',
                  'workflow': {},
                }, acceptable_url);
              } catch (ex) {/* pass */}
            });

            if (!!flow.redirectUrl) {
              window.parent.location = flow.redirectUrl;
              return;
            } else {
              return;
            }
          }
        }

        this._router.navigate(redirect, redirectExtras);
        return;
      }

      if (reload) {
        this.loadData();
        this._popupService.closeAll();
      } else {
        // If no reload, need to run submission rules validation explicitly
        this.runSubmissionRulesValidation(true).then(() => {
          if (showValidationModal) {
            this.processValidationResults();
          }
          this._popupService.closeAll();
          this._crd.detectChanges();
        })
      }

      if (refreshPage) {
        window.location.reload();
      }
    }).catch(error => {
      console.error(error);
      this._popupService.closeAll();
      if (error && error?.message && String(error.message) !== 'undefined') {
          const title = error?.error_title ? error.error_title : UNKNOWN_ERROR_TOAST_TITLE;
          this._alertService.error(error.message, title)
      } else {
        this._alertService.error(UNKNOWN_ERROR_TOAST_BODY, UNKNOWN_ERROR_TOAST_TITLE);
      }
    });
  }

  cellShouldAlignRight(cell: Cell): boolean {
    return !isNaN(cell.value);
  }

  redoManualReview() {
    this._documentFileService.getReviewQueueItemForFileId(this.state.documentFile.id).subscribe((data) => {
      const reviewQueueItemId = data.response.objects[0].reviewQueueItem;
      this._router.navigate(['review', reviewQueueItemId, 'manual_review']);
    },
    error => {
        this._alertService.error('Unable to get Review Queue Item');
    });
  }

  getAutocompleteOptionsForRow(row: Row): Array<AutocompleteOption> {
    if (row.derivedFromTemplate) {
      return this.findItemInTemplateMatchingLabel(row.label)
    } else if (row.mappingBoxValidation) {
      return this.state.autocompleteOptions.concat(this.state.rollupOptions);
    }
    return this.state.autocompleteOptions;
  }

  findItemInTemplateMatchingLabel(label) {
    let options: Array<AutocompleteOption> = [];
    for(const [, lineItem] of this.state.templateMap) {
      if (lineItem.label === label) {
        options.push({
          'id': lineItem.lineItemId,
          'value': lineItem.label,
        })
      }
    }
    return options
}

  onTemplateSourceRowLabelChanged(payload: { value: string; row: Row }): void {
    this.rowLabelChanged(payload.value, payload.row);
  }

  onTemplateSourceRowLabelEditStart(row: Row): void {
    const idx = this.state.sourceRows.indexOf(row);
    if (idx < 0) {
      return;
    }
    this.handleEditRowLabel(idx, row?.itemClass, 'template');
  }

  handleEditSourceRowLabel(rowIdx: number, rowItemClass?: string): void {
    this.handleEditRowLabel(rowIdx, rowItemClass, 'source');
  }

  onTemplateSourceRowCellValueChanged(payload: {
    value: string;
    row: Row;
    cellIdx: number;
  }): void {
    this.doneEditingCell(payload.value, payload.row, payload.cellIdx);
  }

  onTemplateSourceRowCellEditStart(payload: { row: Row; cellIdx: number }): void {
    this.editCell(payload.row, payload.cellIdx, 'template');
  }

  onTemplateSourceRowCellSelect(payload: { row: Row; cellIdx: number }): void {
    const rowIdx = this.state.sourceRows.indexOf(payload.row);
    if (rowIdx < 0) {
      return;
    }
    this.highlightCell(payload.row, payload.cellIdx, rowIdx);
  }

  onTemplateSourceRowCellContextMenu(payload: {
    event: MouseEvent;
    row: Row;
    cell: Cell;
    cellIdx: number;
  }): void {
    const rowIdx = this.state.sourceRows.indexOf(payload.row);
    if (rowIdx < 0) {
      return;
    }
    this.rightClickedCell(
      payload.event,
      payload.row,
      payload.cell,
      payload.cellIdx,
      rowIdx,
      false,
      'template'
    );
  }

  onTemplateSourceRowCellKeyDown(payload: {
    cell: Cell;
    event: KeyboardEvent;
  }): void {
    this.handleKeyDown(payload.cell, payload.event);
  }

  onTemplateSourceRowLabelContextMenu(payload: { event: MouseEvent; row: Row }): void {
    this.stopEvent(payload.event);
    this.contextMenuMode = 'sourceLabel';
    this.contextMenuRow = payload.row;
    this.selectLabel(payload.row);
    this.showContextMenuAtEventLocation(payload.event);
  }

  editLabelAndStopEvent(evt: MouseEvent, row: Row): void {
    this.stopEvent(evt);
    this.hideContextMenu();
    this.onTemplateSourceRowLabelEditStart(row);
  }


  editSourceLabelAndStopEvent(evt: MouseEvent, rowIdx: number, row: Row): void {
    this.stopEvent(evt);
    this.hideContextMenu();
    this.handleEditSourceRowLabel(rowIdx, row?.itemClass);
  }

  onPreviousPeriodCellViewComment(payload: { event: MouseEvent; cell: Cell }): void {
    this.openPreviousPeriodCommentMenu(payload.cell?.sourceComments, payload.event);
  }

  onPreviousPeriodColumnViewComment(payload: { event: MouseEvent; column: Column }): void {
    this.openPreviousPeriodCommentMenu(payload.column?.sourceComments, payload.event);
  }

  private openPreviousPeriodCommentMenu(sourceComments: Array<SourceComment>, event: MouseEvent): void {
    this.stopEvent(event);
    this.contextMenuReadonlyCommentText = sourceComments?.[0]?.comment ?? '';
    this.contextMenuMode = 'previousPeriodViewCommentMenu';
    this.showContextMenuAtEventLocation(event);
  }

  showPreviousPeriodComment(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuMode = 'previousPeriodViewComment';
    this._crd.detectChanges();
  }

  rowLabelChanged(newValue: string, row: Row) {
    if (row.label !== newValue) {
      row.label = newValue;
      if (row.userFacingLabel) {
        row.userFacingLabel = newValue;
      }
      // Clear stale translation so toggle-on shows the newly edited label via fallback
      row.translatedLabel = null;
      // Remember this row was user-edited so grey/lock persists even without a translatedLabel
      row.labelEditedByUser = true;
      // Keep a linked offset row's label in sync (CITI-1274).
      this.state.applyOffsetLabelSync(row);
      this.madeAChange();
      this.storeNewState(SPREAD_ACTION_ROW_LABEL_CHANGED);

      if (newValue && this.hasTranslations()) {
        this._translationService.translateLabel(newValue.trim()).subscribe({
          next: (translated) => {
            row.translatedLabel = translated;
            this._crd.detectChanges();
          },
          error: (err) => { this.logger.error('Failed to translate row label', err); }
        });
      }
    }
    this.stopEditingText();
    this.refreshTabStatusAfterEdit();
  }


  handleEditRowLabel(rowIdx: number, rowItemClass?: string, pane: 'source' | 'template' = 'source') {
    // Derived offset rows have a read-only, derived label (CITI-1274).
    if (this.state.isDerivedOffsetRow(this.state.sourceRows[rowIdx])) {
      return;
    }
    if (rowItemClass === 'supplementalItem') {
      return;
    }
    if (rowItemClass === "ManualInputItem") {
      this.warnBeforeAllowingManualInputLabelEdit(rowIdx, pane);
    } else {
      this.beginEditingRowLabel(rowIdx, pane);
    }
  }

  /**
   * Enters label edit mode for a row, setting both halves of the edit state: the template
   * only renders the input when the row index AND the pane match
   */
  beginEditingRowLabel(rowIdx: number, pane: 'source' | 'template' = 'source') {
    this.editingRowLabelIndex = rowIdx;
    this.labelEditFocusPane = pane;
    this._crd.detectChanges();
  }

  warnBeforeAllowingManualInputLabelEdit(rowIdx: number, pane: 'source' | 'template' = 'source') {
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: "Warning",
        text: 'Renaming this label will update its name across all other spreads for this entity. The change will take effect once the current spread is completed.',
        confirmButtonText: 'Continue'
      },
    }).then((popup: NgxPopupComponent) => {
      popup.addEventListener('close', (data: CustomEvent) => {
        if (data.detail === true) {
          this.beginEditingRowLabel(rowIdx, pane);
        }
      }, {once: true});
    });
  }

  private refreshTabStatusAfterEdit(): void {
    this.validationSubmissionRuleService.syncCurrentStatementTabStatus(this.state);
    this._crd.detectChanges();
  }

  saveChangesForCell(cell: Cell, lineItemId: number, newValue: number) {
    this._statementDataService.changeCellValue(cell, lineItemId, newValue).then(() => {
      return;
    }).catch(err => {
      console.error(err);
    });
  }

  /**
   * Converts a column index to its excel equivalent in alphabet notation.
   *
   * E.g.
   *
   * 0 -> A
   * 1 -> B
   * ...
   * 25 -> Z
   * 26 -> AA
   * ...
   * etc
   *
   * @param idx
   */
  headerForColumnIndex(idx: number): string {
    let num = idx + 1;

    const lettersInAlphabet = 26;
    // Get us to the first letter after all the other stuff. This only works because the char codes
    // for letters are in alphabetic order and start at 65.
    const charCodeOffset = 65;
    let ret = '';
    for (let a = 1, b = lettersInAlphabet; (num -= a) >= 0; a = b, b *= lettersInAlphabet) {
      const charCode = ((num % b) / a) + charCodeOffset;
      ret = String.fromCharCode(charCode) + ret;
    }
    return ret;
  }

  sortStatements(statements: Array<Statement>, taxonomy: Array<TemplateItem>) {
    let sortOrder = {};
    taxonomy.forEach(templateItem => {
      if (templateItem.isDynamicAdjustmentStatement) {
        if (!sortOrder.hasOwnProperty(DYNAMIC_ADJUSTMENT_STATEMENT)) {
          sortOrder[DYNAMIC_ADJUSTMENT_STATEMENT] = {}
        }
        sortOrder[DYNAMIC_ADJUSTMENT_STATEMENT][templateItem.label] = taxonomy.indexOf(templateItem) + 1;
      }
      else if (templateItem['lineItemId'] in TAXONOMY_ID_STATEMENT_TYPE_LOOKUP) {
        sortOrder[TAXONOMY_ID_STATEMENT_TYPE_LOOKUP[templateItem['lineItemId']]] = taxonomy.indexOf(templateItem) + 1;
      }
    });

    return statements.sort((a: Statement, b: Statement): number => {
      if (!sortOrder.hasOwnProperty(a.statementType) || !sortOrder.hasOwnProperty(b.statementType)) {
        return 0;
      }

      const aOrder = a.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? sortOrder[a.statementType][a.uniqueDasName] : sortOrder[a.statementType];
      const bOrder =  b.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? sortOrder[b.statementType][b.uniqueDasName] : sortOrder[b.statementType];

      if (aOrder > bOrder) {
        return 1;
      } else if (aOrder < bOrder) {
        return -1;
      }
      return 0;
    });
  }

  willColumnOverwrite(column: any): boolean {

    if (this.isProjectionOrProforma(column.scenario)) {
      // We do not overwrite, delete, etc. projections.
      return false;
    }

    let doesColumnExist = false;
    this.existingColumns.forEach(element => {
      if (column.preparationType === element.preparationType &&
        column.fiscalYearEnd.includes(element.fiscalYearEnd) &&
        column.reportingPeriod === element.reportingPeriod
      ) {
        doesColumnExist = true;
      }
    });
    return doesColumnExist;
  }

  chooseTemplate(templateIdChosen: string): void {
    const numericTemplateId = parseInt(templateIdChosen, 10);
    this.selectedTemplate = numericTemplateId;

    if (numericTemplateId) {
      this.hasTemplate = true;
      this._crd.detectChanges();
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: true, reAutonorm: false, allowDeletingInsertedRows: false, refreshPage: true }, true);
    }
    return;
  }

  storeNewState(action = ''): void {
    const newStateAdded = this._spreadingHistoryService.addNewState(Object.assign({}, this.state), action);
    if (newStateAdded) {
      this.noMoreUndos = false;
    }
  }

  /**
   * Restores the state from the provided history data.
   * @param spreadingHistoryData - The history data containing the state to restore
   */
  private restoreState(spreadingHistoryData: SpreadingState): void {
    if (!spreadingHistoryData) {
      return;
    }

    const newStateValues = spreadingHistoryData['newStateValues'];
    this.noMoreUndos = spreadingHistoryData['noMoreUndos'];
    this.noMoreRedos = spreadingHistoryData['noMoreRedos'];
    this.state.rows = newStateValues.rows;
    this.state.sourceRows = newStateValues.sourceRows;
    this.state.statement.rows = newStateValues.sourceRows;
    this.state.referenceRows = newStateValues.referenceRows;
    this.state.columns = newStateValues.columns;
    this.state.statement.columns = newStateValues.columns;
    this.state.rebuildRowReferences();
  }

  undo(): void {
    this.undoButtonLoading = true;
    setTimeout(() => {
      this._crd.detectChanges();

      const spreadingHistoryData = this._spreadingHistoryService.undo();
      this.restoreState(spreadingHistoryData);
      
      this.undoButtonLoading = false;
      this.state.calculate();
      this._crd.detectChanges();
    }, 300);
  }

  redo(): void {
    this.redoButtonLoading = true;
    setTimeout(() => {
      const spreadingHistoryData = this._spreadingHistoryService.redo();
      this.restoreState(spreadingHistoryData);
      
      this.redoButtonLoading = false;
      this.state.calculate();
      this._crd.detectChanges();
    }, 300);
  }

  updateCurrencyForCompany() {
    this._borrowerService.setSetting('currency', this.state.documentFile.currency, this.companyObject);
  }

  reAutonormDocument() {
    if (this.reSpreadInProgress) {
      return
    }
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'RESET MAPPINGS',
        text: 'This will re-run the autonormalization algorithms and reset all mappings for this document, removing any manual adjustments you have made to the spread. Are you sure you want to reset the mappings for this document?',
        confirmButtonText: 'Yes'
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: true, allowDeletingInsertedRows: false, refreshPage: false}, true);
          }
        }
      },
    });
  }

  private stopEvent(evt = null) {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  }

  /**
   * Turns off the flag for showing the mapping box and other related
   * attributes on all source rows
   */
  private resetMappingBoxConfig() {
    this.state.sourceRows.forEach(r => {
      r.showMappingBox = false;
      r.mappingBoxValidation = false;
    });
  }

  private controlKey(evt) {
    if (IS_MAC) {
      return evt.metaKey; // command/apple key
    } else { // assume windows
      return evt.ctrlKey; // use ctrl key
    }
  }

  private resetDocumentViewSourceBox() {
    this.viewSourceBox = null;
    this.viewSourcePage = null
  }


  toggleIncludeItem(row) {
    this.state.calculate();
    this.madeAChange();
    this._crd.detectChanges();
  }

  weightInputChange(row){
    try {
      row.weightDecimal = +((parseFloat(row.weightPercentage) / 100).toFixed(2));
      this.madeAChange();
    } catch (err){
      this.logger.error('Failed to convert weight percentage to decimal: ' + err?.message, {'errorObject': err});
    }
  }

  isCellEditingDisabled(row) {
    // Synthetic prior-period rows are read-only history.
    if (row?.isPreviousOnlyRow) {
      return true;
    }
    // Derived offset rows are read-only everywhere (CITI-1274).
    if (this.state?.isDerivedOffsetRow(row)) {
      return true;
    }
    if (!this.isDASStatement){
      return false
    }
    return ['AdjustedStandardItem', 'NonAdjustableStandardItem'].includes(row?.itemClass) ||
      ['asReported', 'customCalculation'].includes(row?.dynamicAdjustmentStatementItemType)
  }

  /*
   * Get display rounding label from current selected rounding key
   */
  getDisplayRounding() {
    const roundingSelection = <ScaleType>(
      this.state?.reviewQueueItem?.multiplyByFactor
    );
    return ROUNDING_MAP[roundingSelection];
  }

  /*
   * Get rounded value from display rounding/multiplication
   */
  getRoundedValue(cellValue, row) {
    const roundedVal = this._roundingService.roundCellValue(this.state, row, cellValue);
    return roundedVal;
  }

  /*
   * Check if scenario is projection or proforma
   */
  isProjectionOrProforma(scenario: string): boolean {
    return scenario === SCENARIO_TYPE_PROJECTION || scenario === SCENARIO_TYPE_PROFORMA;
  }

  protected readonly DYNAMIC_ADJUSTMENT_STATEMENT = DYNAMIC_ADJUSTMENT_STATEMENT;

  private navigateToValidationIssue(navigationRequest: NavigationRequest): void {
    const { statementId, statementDate, periodKeyUuid, rowRef } = navigationRequest;
    
    // Find the target statement
    const targetStatement = this.relatedStatements?.find(s => s.id === statementId);
    if (!targetStatement) {
      console.warn('Target statement not found for navigation');
      return;
    }

    // If we need to switch to a different statement, store navigation info in localStorage
    if (targetStatement.id !== this.state.statement.id) {
      const navigationData = <NavigationRequest>{
        navigateTo: 'validation',
        rowRef: rowRef,
        statementDate: statementDate,
        periodKeyUuid: periodKeyUuid
      };
      localStorage.setItem('navigateToSubmissionRuleResult', JSON.stringify(navigationData));
      this.switchToStatement(targetStatement);
      return;
    }

    this.executeNavigation(navigationRequest);
  }

  private executeNavigation(navigationRequest: NavigationRequest): void {
    const { statementDate, periodKeyUuid, rowRef } = navigationRequest;
    
    let targetColumnIndex = -1;
    if (periodKeyUuid) {
      targetColumnIndex = this.state.columns.findIndex(col => col.periodKeyUuid === periodKeyUuid);
    }
    if (targetColumnIndex === -1) {
      targetColumnIndex = this.state.columns.findIndex(col => col.statementDate === statementDate);
    }
    if (targetColumnIndex >= 0 && targetColumnIndex !== this.state.getLastSelectedItemIndex('column')) {
      this.goToColumn(targetColumnIndex);
    }

    this.scrollToSpecificValidationIssueByRef(rowRef);
  }

  private scrollToSpecificValidationIssueByRef(rowRef: string): void {
    if (!rowRef) {
      console.warn('No rowRef provided for navigation');
      return;
    }
    
    // Scroll to target element using the data-bs-target attribute
    const targetRow = document.querySelector(`[data-bs-target="#${rowRef}"]`);
    if (targetRow) {
      targetRow.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else {
      console.warn(`Template row with ref ${rowRef} not found, falling back to general scrolling`);
    }
  }

  private navigateToSubmissionRuleResult(): void {
    const pendingNavigation = localStorage.getItem('navigateToSubmissionRuleResult');
    if (pendingNavigation) {
      try {
        const navigationData = JSON.parse(pendingNavigation);
        if (navigationData.navigateTo === 'validation') {
          setTimeout(() => {
            const navigationRequest = {
              statementDate: navigationData.statementDate,
              periodKeyUuid: navigationData.periodKeyUuid,
              rowLabel: navigationData.rowLabel,
              rowRef: navigationData.rowRef
            };
            this.executeNavigation(navigationRequest);
          });
        }
      } catch (error) {
        console.warn('Invalid navigation data in localStorage:', error);
      } finally {
        localStorage.removeItem('navigateToSubmissionRuleResult');
      }
    }
  }

  get countOfUncategorizedLineItems() {
    return (this.state?.sourceRows ?? []).filter(
        row => !row.header && row.itemClass !== 'supplementalItem' && !this.isSourceRowLinkedToTemplate(row)
    ).length;
  }
}